import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LayoutComponent } from "../../../../../components/protected/layout-components/LayoutComponent";
import {
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  Check2Circle,
  ChevronLeft,
  Plus,
  QuestionCircle,
  Trash,
} from "react-bootstrap-icons";
import {
  AIChatIconColored,
  AlertIconFill,
  ArrowForwardIcon,
  CommentIcon,
  MoreIcon,
  ReplyAllIcon,
  ReplyIcon,
  SearchIcon,
  TrashIcon,
} from "../../../../../components/protected/Icons/IconsLib";
import FilterIcon from "@spectrum-icons/workflow/Filter";
import {
  Flex,
  Content,
  ProgressCircle,
  Button,
  View,
  Link,
  ListBox,
  IllustratedMessage,
  Heading,
  Item,
  ActionButton,
  DialogTrigger,
  DialogContainer,
  Dialog,
  TextField,
  DatePicker,
  Text,
  Checkbox,
  Divider,
  Form,
  Footer,
  Provider,
  Grid,
} from "@adobe/react-spectrum";
import { ToastQueue } from '@adobe/react-spectrum';
import NotFound from "@spectrum-icons/illustrations/NotFound";
import moment from "moment";
import { useAsyncList } from "react-stately";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { useViewInfo } from "../../../../../hooks/UseViewInfo";
import { useDeviceDetection } from "../../../../../hooks/UseDeviceDetection";
import { useTranslation } from "react-i18next";
import { AtollonEvent, publish } from "../../../../../infra/events/Events";
import { useReefConfig } from "../../../../../hooks/UseReefConfig";
import CardComponentLarge from "./MessagesCardComponentLarge";
import MessageAttachment from "./MessagesAttachmentComponent";
import { useMessageCount } from "../../../../../contexts/MessageCountContext";
import NewMessageComponent from "./NewMessageComponent";
import MessagesListComponent from "./Messages_List_Component";
import { BulkSelectionProvider } from "../../../../../components/protected/layout-components/BulkSelectCheckbox";
import { useMessagesState } from "../../../../../hooks/useMessagesState";
import { useMessageOperations } from "../../../../../hooks/useMessageOperations";
import { useMessageNodeId } from "../../../../../hooks/useMessageNodeId";
import {
  addKey,
  useKeyboardShortcuts,
} from "../../../../../hooks/UseKeyboardShortcuts";
import {
  invokeFinderApp,
  invokeNewMessageCreating,
} from "../../../../../utils/NavigationUtils";
import ConceptCardComponent from "./ConceptCardComponent";
import {
  findMessageIndexById,
  getNextMessageAfterDeletion,
} from "../../utils/messageUtils";
import {
  joinPaths,
  normalizeAtollonNumericId,
  truncateText,
} from "../../../../../utils/helperUtil";
import {
  deriveUiMessageState,
  getDiscussionTargetMessageId,
  isCommentVisible,
  isDeleteVisible,
  isMarkDoneVisible,
  isResolveVisible,
  requiresDeleteConfirmation,
} from "../../../../../utils/messageResolution";
import { ContextMoveDialog } from "../../../../../components/protected/Context/ContextMoveDialog";
import { ResolveMessageOptionsDialog } from "../../../../../components/protected/Context/ResolveMessageOptionsDialog";
import { FPADataTypes } from "../../../../../infra/protected/FPA/FPAData";
import type { ContextTypeKind } from "../../../../../components/protected/Search/ContextSelection/types";
import styles from "./messages_component.module.css";
import { CommentChatWindow } from "./CommentChatWindow";
import { EmailSenderContactBadge } from "../../../../../components/protected/messaging/EmailSenderContactBadge/EmailSenderContactBadge";
import { GetFolderRequest } from "../../../../../services/soap/project/requests/GetFolderRequest";
import { GetProjectRequest } from "../../../../../services/soap/project/requests/GetProjectRequest";

/**
 * Helper function to extract recipient value from various data formats
 * Handles: arrays (of strings or objects), strings, and objects with CONTENT property
 */
const getRecipientValue = (data: any): string | null => {
  if (!data) return null;

  if (Array.isArray(data) && data.length > 0) {
    // Handle array of objects with CONTENT property or array of strings
    const values = data
      .map((item) => {
        if (typeof item === "string") {
          return item.trim();
        }
        if (item?.CONTENT && typeof item.CONTENT === "string") {
          return item.CONTENT.trim();
        }
        // Some SOAP shapes map <contact> to `contact` or `contactName`, not `CONTENT`
        // (used by notify/user recipients).
        const alt =
          (typeof item?.contact === "string" && item.contact.trim()
            ? item.contact
            : undefined) ??
          (typeof item?.contactName === "string" && item.contactName.trim()
            ? item.contactName
            : undefined) ??
          (typeof item?.userName === "string" && item.userName.trim()
            ? item.userName
            : undefined) ??
          (typeof item?.name === "string" && item.name.trim() ? item.name : undefined) ??
          (typeof item?.emailText === "string" && item.emailText.trim()
            ? item.emailText
            : undefined) ??
          (typeof item?.id === "string" && item.id.trim() ? item.id : undefined);
        if (alt) return String(alt).trim();
        return null;
      })
      .filter(Boolean);

    return values.length > 0 ? values.join(", ") : null;
  }
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data.CONTENT && typeof data.CONTENT === "string" && data.CONTENT.trim()) {
    return data.CONTENT.trim();
  }
  if (data && typeof data === "object") {
    const alt =
      (typeof data.contact === "string" && data.contact.trim() ? data.contact : undefined) ??
      (typeof data.contactName === "string" && data.contactName.trim()
        ? data.contactName
        : undefined) ??
      (typeof data.userName === "string" && data.userName.trim()
        ? data.userName
        : undefined) ??
      (typeof data.name === "string" && data.name.trim() ? data.name : undefined) ??
      (typeof data.emailText === "string" && data.emailText.trim()
        ? data.emailText
        : undefined) ??
      (typeof data.id === "string" && data.id.trim() ? data.id : undefined);
    if (alt) return String(alt).trim();
  }
  return null;
};

/**
 * Component to render the "To" and "Cc" recipient fields
 * Only renders fields that have valid data
 */
const RecipientToField = ({ recipient }: { recipient: any }) => {
  const { t } = useTranslation();
  const toValue = getRecipientValue(recipient?.TO);
  const ccValue = getRecipientValue(recipient?.CC);
  //console.log("ccValue", recipient?.CC);
  // Don't render anything if neither To nor Cc has data
  if (!toValue && !ccValue) return null;

  return (
    <Flex
      direction="row"
      justifyContent="start"
      alignItems="center"
      width="100%"
      gap="size-200"
      UNSAFE_className={styles.email_to}
    >
      {toValue && (
        <View>
          <span title={toValue} style={{ color: "#728289" }}>
            {t("to", { ns: "layout_components", defaultValue: "To" })}
          </span>
          &nbsp;{truncateText(toValue, 150)}
        </View>
      )}
      {ccValue && (
        <View>
          <span title={ccValue} style={{ color: "#728289" }}>
            {t("cc", { ns: "layout_components", defaultValue: "Cc" })}
          </span>
          &nbsp;{truncateText(ccValue, 150)}
        </View>
      )}
    </Flex>
  );
};

const RecipientNotifyField = ({ recipient }: { recipient: any }) => {
  const notifyValue = getRecipientValue(recipient?.USER ?? recipient?.NOTIFY);
  if (!notifyValue) return null;

  return (
    <Flex
      direction="row"
      justifyContent="start"
      alignItems="center"
      width="100%"
      gap="8px"
      wrap
      UNSAFE_className={styles.notify_recipient_row}
    >
      <View>
        {/* <span title={notifyValue} style={{ color: "#728289" }}>
          {t("notify", { ns: "layout_components", defaultValue: "Notify" })} : 
        </span> */}
        {truncateText(notifyValue, 150)}
      </View>
    </Flex>
  );
};

const RecipientDetailField = ({
  recipient,
  omitNotify,
}: {
  recipient: any;
  /** When true, Notify is rendered separately (e.g. under context links). */
  omitNotify?: boolean;
}) => {
  const { t } = useTranslation();
  const recipientRows = [
    {
      label: t("to", { ns: "layout_components", defaultValue: "To" }),
      value: getRecipientValue(recipient?.TO),
    },
    {
      label: t("cc", { ns: "layout_components", defaultValue: "Cc" }),
      value: getRecipientValue(recipient?.CC),
    },
    {
      label: t("bcc", { ns: "layout_components", defaultValue: "Bcc" }),
      value: getRecipientValue(recipient?.BCC),
    },
    ...(omitNotify
      ? []
      : [
          {
            label: t("notify", { ns: "layout_components", defaultValue: "Notify" }),
            value: getRecipientValue(recipient?.USER ?? recipient?.NOTIFY),
          },
        ]),
  ].filter((row) => row.value);

  if (recipientRows.length === 0) return null;

  return (
    <Flex
      direction="column"
      alignItems="stretch"
      justifyContent="start"
      gap="size-100"
      UNSAFE_className={styles.mobile_detail_meta_section}
    >
      {recipientRows.map((row) => (
        <Flex
          key={row.label}
          direction="row"
          alignItems="start"
          justifyContent="start"
          gap="size-100"
          UNSAFE_className={styles.mobile_detail_meta_row}
        >
          <View UNSAFE_className={styles.mobile_detail_meta_label}>
            {row.label}:
          </View>
          <View UNSAFE_className={styles.mobile_detail_meta_value}>
            {row.value}
          </View>
        </Flex>
      ))}
    </Flex>
  );
};

const sameMessageId = (
  a: string | number | undefined | null,
  b: unknown,
): boolean => {
  const na = normalizeAtollonNumericId(a);
  const nb = normalizeAtollonNumericId(b);
  if (na != null && nb != null) return na === nb;
  const sa = a != null ? String(a).trim() : "";
  const sb = b != null && b !== "" ? String(b).trim() : "";
  if (!sa || !sb) return false;
  return sa === sb;
};

/**
 * Merge list-row RECIPIENT with getMessage detail only when the detail payload
 * belongs to the currently selected message. Otherwise the previous row's detail
 * can leak (notify users showing on every message).
 */
const resolveRecipientForDisplay = (
  selectedMessageId: string | number | undefined | null,
  listRecipient: any,
  detailMessage: any,
): any => {
  const detailId =
    detailMessage?.id ?? detailMessage?.MESSAGE?.id ?? detailMessage?.messageId;
  const detailMatches = sameMessageId(selectedMessageId, detailId);

  if (detailMatches && detailMessage?.RECIPIENT != null) {
    return detailMessage.RECIPIENT;
  }
  return listRecipient;
};

/**
 * Notify users must not be taken from list rows: in threaded views the API often
 * repeats the same RECIPIENT.USER on every row. Only the getMessage payload for the
 * selected message id is authoritative for notify.
 */
const resolveNotifyRecipientForDisplay = (
  selectedMessageId: string | number | undefined | null,
  detailMessage: any,
): any => {
  const detailId =
    detailMessage?.id ?? detailMessage?.MESSAGE?.id ?? detailMessage?.messageId;
  const detailMatches = sameMessageId(selectedMessageId, detailId);

  if (!detailMatches) {
    return undefined;
  }
  return detailMessage?.RECIPIENT;
};

const ContextPathLinks = ({
  message,
  styles,
}: {
  message?: any;
  styles: Record<string, string>;
}) => {
  // Activity navigation: prefer the actual numeric activity node id (`activityId`,
  // matching how `SearchUtils.processHistoryData` sets the activity context id).
  // `activityRefId` is the activity's display reference code (e.g. used as a
  // breadcrumb subtitle in search results) and may be non-numeric — feeding it
  // to the Finder app makes `+id` resolve to NaN and `GetContextPath` errors out
  // after the app opens. We only fall back to it when it is itself numeric.
  const resolveActivityId = (msg: any): string => {
    const candidates = [msg?.activityId, msg?.activityRefId];
    for (const candidate of candidates) {
      const value = String(candidate ?? "").trim();
      if (value && value !== "0" && /^\d+$/.test(value)) {
        return value;
      }
    }
    return "";
  };

  const contextItems = [
    {
      key: "folder",
      id: String(message?.folderId || "").trim(),
      name: message?.folderName,
      contextType: "folder",
    },
    {
      key: "project",
      id: String(message?.projectId || "").trim(),
      name: message?.projectName,
      contextType: "project",
    },
    {
      key: "activity",
      id: resolveActivityId(message),
      name: message?.activityName,
      contextType: "activity",
    },
  ].filter((item) => item.name);

  if (contextItems.length === 0) return null;

  // Only render as a clickable link when the resolved id is a positive integer
  // node id; otherwise fall back to plain text so the Finder cannot be invoked
  // with an invalid contextId (which previously caused activity-level errors).
  const isLinkableId = (id: string): boolean =>
    !!id && id !== "0" && /^\d+$/.test(id);

  return (
    <Flex direction={"row"} alignItems={"center"} justifyContent={"start"} gap={"6px"} wrap>
      {contextItems.map((item, index) => (
        <Fragment key={item.key}>
          {index > 0 && (
            <View UNSAFE_className={styles.icon_chevron_right}>{">"}</View>
          )}
          {isLinkableId(item.id) ? (
            <button
              type="button"
              className={styles.context_path_button}
              onClick={() => {
                publish(AtollonEvent.COMMUNICATION_MOBILE_MENU, { show: true });
                invokeFinderApp(item.id, item.contextType);
              }}
            >
              <span className={styles.address}>{item.name}</span>
            </button>
          ) : (
            <View UNSAFE_className={styles.address}>{item.name}</View>
          )}
        </Fragment>
      ))}
    </Flex>
  );
};

const resolveContextIdForMessage = (message: any): string => {
  const id =
    // Prefer the most specific context (Activity > Project > Folder).
    // Never use *RefId here; those are display/reference codes, not context node ids.
    message?.activityId ??
    message?.projectId ??
    message?.folderId ??
    message?.MESSAGE?.activityId ??
    message?.MESSAGE?.projectId ??
    message?.MESSAGE?.folderId ??
    "";
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? String(n) : "";
};

const resolveContextTypeForMessage = (message: any): ContextTypeKind => {
  const source = message?.MESSAGE ?? message ?? {};
  if (source?.activityId) return "ACTIVITY";
  if (source?.projectId) return "PROJECT";
  if (source?.folderId) return "FOLDER";
  return "";
};

/**
 * Centered placeholder shown inside the detail pane while two or more rows
 * are bulk-selected. Client spec (Apple Mail parity): suppress the detail
 * view and just print the selection count; any message-specific actions
 * (reply/forward/comment/resolve/mark-done) are intentionally absent.
 */
const BulkSelectionPlaceholder = ({
  count,
  styles,
  label,
}: {
  count: number;
  styles: Record<string, string>;
  label: string;
}) => (
  <Flex
    direction="column"
    alignItems="center"
    justifyContent="center"
    width="100%"
    height="100%"
    UNSAFE_className={styles.bulk_selection_placeholder}
  >
    <View UNSAFE_className={styles.bulk_selection_placeholder_count}>
      {count}
    </View>
    <View UNSAFE_className={styles.bulk_selection_placeholder_label}>
      {label}
    </View>
  </Flex>
);

export interface CommActionHandlers {
  onNewEmail: () => void;
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
  onRefresh?: () => void;
  onOpenSearch?: () => void;
  onOpenFilter?: () => void;
  onResolveTrigger: () => void;
  onDeleteTrigger: () => void;
  onMoveTrigger?: () => void;
  onComment?: () => void;
  showReplyAction?: boolean;
  showForwardAction?: boolean;
  showCommentAction?: boolean;
  showDeleteAction?: boolean;
  shouldConfirmDelete?: boolean;
  showResolveAction?: boolean;
  showMarkDoneAction?: boolean;
  showMoveAction?: boolean;
  /**
   * Number of rows currently ticked via the bulk-select checkbox. `0` means
   * the user is in single-select mode. Exposed so the toolbar can render a
   * "N selected" chip and decide whether bulk-only controls (archive icon)
   * should appear.
   */
  bulkSelectedCount?: number;
}

type MobileSwipeAction = "reply" | "replyAll" | "forward" | "comment" | "move";

interface MessagesProps {
  setShowMobileMenu?: (show: boolean) => void;
  messageType:
  | "todo"
  | "messages"
  | "sent"
  | "trash"
  | "corporate"
  | "concept"
  | "";
  customTreeHandle?: number;
  onRegisterHandlers?: (handlers: CommActionHandlers | null) => void;
  onRegisterMoveMessage?: (
    moveHandler:
      | ((messageId: string, newNode: string | number) => Promise<boolean>)
      | null,
  ) => void;
  onDesktopRowDragStart?: (message: any | null) => void;
  onDetailVisibilityChange?: (showDetail: boolean) => void;
}
export interface Character {
  id: string;
  seen: string;
  sender?: string;
  subject?: string;
  date?: string;
  mailFrom?: string;
}

const MessagesList = ({
  setShowMobileMenu,
  messageType,
  customTreeHandle,
  onRegisterHandlers,
  onRegisterMoveMessage,
  onDesktopRowDragStart,
  onDetailVisibilityChange,
}: MessagesProps) => {
  const { t } = useTranslation();
  const { store, messageService, mainService, accessService, projectService } =
    useDependency();
  const { message_preview } = useReefConfig();
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection(isMobile);
  const shouldUseDesktopLayout =
    !deviceInfo.shouldUseMobileLayout() ||
    ((deviceInfo.isTablet || deviceInfo.isIPad) &&
      deviceInfo.orientation === "landscape");
  const isCompact = !shouldUseDesktopLayout;
  const useMobileCompactLayout = isCompact;
  const compactDetailTopOffset = isCompact && !isMobile ? 124 : 0;
  const { refreshMessageCounts } = useMessageCount();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [isResolveOptionsOpen, setIsResolveOptionsOpen] = useState(false);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [isCommentChatOpen, setIsCommentChatOpen] = useState(false);
  const [commentChatTarget, setCommentChatTarget] = useState<any>(null);
  const [bulkSelectedKeys, setBulkSelectedKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [commentChatCurrentContextId, setCommentChatCurrentContextId] = useState<
    string | number | undefined
  >(undefined);
  const [mobileDetailIframeHeight, setMobileDetailIframeHeight] =
    useState<number>(520);
  const selectedMessageIdRef = useRef<string>("");
  const pendingMobileSwipeActionRef = useRef<{
    messageId: string;
    action: MobileSwipeAction;
  } | null>(null);
  const detailLoadGenerationRef = useRef(0);
  const _handlersRef = useRef<CommActionHandlers>({
    onNewEmail: () => { },
    onReply: () => { },
    onReplyAll: () => { },
    onForward: () => { },
    onResolveTrigger: () => { },
    onDeleteTrigger: () => { },
    onMoveTrigger: () => { },
    showResolveAction: false,
    showMarkDoneAction: false,
    showMoveAction: false,
  });

  // Custom hooks for state management and operations
  const { state, refs, actions } = useMessagesState();
  const uiMessageState = useMemo(
    () =>
      deriveUiMessageState({
        listType: messageType,
        selectedMessage: state.selectedDataRow,
        detailMessage: state.messageContent?.MESSAGE,
        defaultMessageNodeId: state.messageNodeId,
      }),
    [messageType, state.messageContent?.MESSAGE, state.messageNodeId, state.selectedDataRow],
  );
  const showResolveAction = isResolveVisible(uiMessageState);
  const showMarkDoneAction = isMarkDoneVisible(uiMessageState);
  const showCommentAction = isCommentVisible(uiMessageState);
  const showDeleteAction = isDeleteVisible(uiMessageState);
  const showMoveAction =
    !!state.selectedDataRow?.id && uiMessageState.viewKind === "original";
  const shouldConfirmDelete = requiresDeleteConfirmation(uiMessageState);
  const showReplyAction = uiMessageState.supportsReply;
  const showForwardAction = uiMessageState.supportsForward;
  const hasBulkSelection = bulkSelectedKeys.size > 0;
  const bulkSelectedCount = bulkSelectedKeys.size;
  // Apple-Mail style: suppress the message detail only once the user has
  // ticked two or more checkboxes. A single checkbox tick keeps the currently
  // opened detail visible so single-select flows remain untouched.
  const isBulkDetailSuppressed = bulkSelectedCount >= 2;
  const bulkSelectedRows = useMemo(
    () =>
      state.rowsData.filter((row: any) =>
        bulkSelectedKeys.has(String(row?.id ?? "")),
      ),
    [bulkSelectedKeys, state.rowsData],
  );
  const handleBulkSelectionChange = useCallback(
    (id: string, nextSelected: boolean) => {
      const key = String(id ?? "").trim();
      if (!key) return;
      setBulkSelectedKeys((current) => {
        const next = new Set(current);
        if (nextSelected) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    [],
  );


  const senderContactBadgeRow = useMemo(
    () => ({
      ...state.selectedDataRow,
      senderContactId:
        state.selectedDataRow?.senderContactId ??
        state.messageContent?.MESSAGE?.senderContactId,
      status:
        state.selectedDataRow?.status ??
        state.messageContent?.MESSAGE?.status,
      type:
        state.selectedDataRow?.type ??
        state.messageContent?.MESSAGE?.type,
    }),
    [
      state.messageContent?.MESSAGE?.senderContactId,
      state.messageContent?.MESSAGE?.status,
      state.messageContent?.MESSAGE?.type,
      state.selectedDataRow,
    ],
  );

  // Stable ref for total record count — avoids stale closure in useAsyncList
  const totalRecordsRef = useRef<number>(0);
  // Tracks whether useAsyncList has already fired its auto-load on mount.
  // Prevents the useEffect([messageType]) from calling setFilterText on first mount,
  // which would duplicate the auto-load and cause 2× API calls.
  const loadInitiatedRef = useRef<boolean>(false);
  // Incremented each time a fresh load is requested (reset or messageType change).
  // Any in-flight load that reads a stale generation is discarded, preventing
  // duplicate data from concurrent requests.
  const loadGenerationRef = useRef<number>(0);

  // Initialize messageNodeId using custom hook
  // messageNodeId is per-user (not per-messageType), so only fetch once on mount.
  useMessageNodeId({
    store,
    accessService,
    setMessageNodeId: actions.setMessageNodeId,
    dependencies: [],
  });

  const {
    getMessageListCount,
    loadMessages,
    getEmailContent,
    deleteMessage: deleteMessageOp,
    moveMessage: moveMessageOp,
    utils,
    PAGE_SIZE,
  } = useMessageOperations({
    store,
    messageService,
    mainService,
    messageType,
    refs,
    messageNodeId: state.messageNodeId,
    treeHandle: customTreeHandle,
  });

  const clearSelectedMessageDetail = useCallback(() => {
    detailLoadGenerationRef.current += 1;
    selectedMessageIdRef.current = "";
    actions.setMessageContent("");
  }, [actions]);

  const loadSelectedMessageDetail = useCallback(
    async (selectedRow: any) => {
      const targetId = String(selectedRow?.id ?? "").trim();
      if (!targetId) return null;

      selectedMessageIdRef.current = targetId;
      actions.setMessageContent("");
      const requestGeneration = ++detailLoadGenerationRef.current;
      const messageContent = await getEmailContent(selectedRow);

      if (
        requestGeneration !== detailLoadGenerationRef.current ||
        selectedMessageIdRef.current !== targetId
      ) {
        return null;
      }

      actions.setMessageContent(messageContent);
      return messageContent;
    },
    [actions, getEmailContent],
  );

  useEffect(() => {
    actions.setShowListName(customTreeHandle ? "messages" : messageType);

    if (!loadInitiatedRef.current) {
      // First mount: useAsyncList fires load automatically — don't trigger a second one.
      loadInitiatedRef.current = true;
      return;
    }

    // messageType changed after mount — reset state and reload.
    actions.setRowsData([]);
    resetMessageListState("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customTreeHandle, messageType]);

  // Minimum swipe distance (in pixels)
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    actions.setTouchEnd(null); // Reset touch end
    actions.setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    actions.setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!state.touchStart || !state.touchEnd) return;

    const distance = state.touchStart - state.touchEnd;
    const isRightSwipe = distance < -minSwipeDistance;

    // Handle right swipe (swipe from left to right)
    if (isRightSwipe) {
      // Navigate back to list view
      setShowMobileMenu?.(true);
      actions.setShowDetail(false);
    }

    // You can also handle left swipe for other actions if needed
    // const isLeftSwipe = distance > minSwipeDistance;
    // if (isLeftSwipe) {
    //   // Handle left swipe
    // }
  };

  const messageTabs = [
    {
      key: "all",
      label: t("All", { ns: "layout_components", defaultValue: "All" }),
      color: "#1976D2",
      count: state.totalMessageListRec,
      active: true,
    },
    {
      key: "internal",
      label: t("Internal", {
        ns: "layout_components",
        defaultValue: "Internal",
      }),
      color: "#F44336",
      count: state.totalMessageListRec,
      active: false,
    },
    {
      key: "external",
      label: t("External", {
        ns: "layout_components",
        defaultValue: "External",
      }),
      color: "#8BC34A",
      count: state.totalMessageListRec,
      active: false,
    },
    {
      key: "ai_chat",
      label: t("AI chat", {
        ns: "layout_components",
        defaultValue: "AI chat",
      }),
      color: "#14AFF5",
      count: state.totalMessageListRec,
      active: false,
    },
  ];

  let listMessageList = useAsyncList<Character>({
    // Capture generation at call start — if it changes before we finish, discard results.
    async load({ cursor, filterText }) {
      const myGeneration = loadGenerationRef.current;

      // Use ref so early-exit never reads stale closure state
      if (cursor && Number(cursor) * PAGE_SIZE >= totalRecordsRef.current) {
        actions.setShowLoader(false);
        actions.setShowLoaderDetail(false);
        return { items: [], cursor: undefined };
      }

      try {
        const currentPage = cursor ? parseInt(cursor) : 0;
        const offSet = PAGE_SIZE * currentPage;
        refs.offSetLimitRef.current = offSet;

        // Only fetch total count on first page to avoid repeated API calls + re-renders
        let totalRecords: number;
        if (currentPage === 0) {
          const rawCount = await getMessageListCount(filterText || "");
          // Discard if a newer load has started (e.g., messageType changed mid-flight)
          if (loadGenerationRef.current !== myGeneration) {
            return { items: [], cursor: undefined };
          }
          totalRecords = rawCount ? Number(rawCount) : 0;
          totalRecordsRef.current = totalRecords;
          actions.setTotalMessageListRec(totalRecords);
          actions.setNavigationIndex(0);
        } else {
          totalRecords = totalRecordsRef.current;
        }

        let listMessages = await loadMessages(filterText || "");
        // Discard if a newer load has started
        if (loadGenerationRef.current !== myGeneration) {
          return { items: [], cursor: undefined };
        }

        actions.appendRowsData(listMessages);
        if (currentPage === 0) {
          actions.setSelectedKeys(new Set([listMessages[0]?.id]));
          actions.setSelectedDataRow(listMessages[0]);

          //SET SELECTED ROW EMAIL CONTENT
          if (listMessages[0]) {
            selectedMessageIdRef.current = String(listMessages[0].id ?? "").trim();
            await loadSelectedMessageDetail(listMessages[0]);
          }
        }

        return {
          items: listMessages,
          cursor:
            offSet + PAGE_SIZE < totalRecords
              ? String(currentPage + 1)
              : undefined,
        };
      } catch (error) {
        console.error("Error loading messages:", error);
        // Turn off loader even on error
        actions.setShowLoader(false);
        actions.setShowLoaderDetail(false);
        return { items: [], cursor: undefined };
      } finally {
        if (loadGenerationRef.current === myGeneration) {
          actions.setShowLoader(false);
          actions.setShowLoaderDetail(false);
        }
      }
    },
  });

  const resetMessageListState = (filterText: string) => {
    loadGenerationRef.current++; // Invalidate any in-flight loads from the previous cycle
    clearSelectedMessageDetail();
    setBulkSelectedKeys(new Set());
    actions.resetMessageList();
    actions.setShowLoader(true); // Show loader when resetting
    refs.offSetLimitRef.current = 0;
    totalRecordsRef.current = 0; // Reset count so early-exit doesn't fire prematurely on next load
    listMessageList.setFilterText(filterText);
  };

  const searchMessageList = useCallback(async () => {
    refs.filterParamRef.current = {
      createdFrom: "",
      createdTo: "",
      sender: "",
      subject: "",
      newOnly: false,
      starred: false,
    };
    resetMessageListState(refs.searchedTextRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadMessageList = useCallback(() => {
    refs.filterParamRef.current = {
      createdFrom: "",
      createdTo: "",
      sender: "",
      subject: "",
      newOnly: false,
      starred: false,
    };
    resetMessageListState("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterMessageList = useCallback(() => {
    resetMessageListState("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleListRowClick = async (rowId: any): Promise<void> => {
    const currentKey = rowId?.currentKey;
    if (!currentKey || state.selectedKeys?.has(currentKey)) return;

    actions.setShowLoaderDetail(true);
    const selectedRow = state.rowsData.find((item: any) => item.id === currentKey);
    if (!selectedRow) {
      actions.setSelectedDataRow(null);
      actions.setSelectedKeys(new Set());
      clearSelectedMessageDetail();
      actions.setShowLoaderDetail(false);
      return;
    }

    actions.setSelectedKeys(new Set([currentKey]));
    actions.setSelectedDataRow(selectedRow);
    selectedMessageIdRef.current = String(selectedRow.id ?? "").trim();

    const recIndex = findMessageIndexById(state.rowsData, currentKey);
    actions.setNavigationIndex(recIndex);

    await loadSelectedMessageDetail(selectedRow);
    actions.setShowLoaderDetail(false);
  };

  const handleListRowLargeClick = useCallback(
    async (rowId: any): Promise<void> => {
      const currentKey =
        rowId?.currentKey ??
        (typeof rowId === "string" ? rowId : state.selectedDataRow?.id);
      if (!currentKey) return;

      actions.setShowLoaderDetail(true);
      const normalizedSelectedKeys =
        rowId instanceof Set
          ? rowId
          : new Set([currentKey]);
      actions.setSelectedKeys(normalizedSelectedKeys);
      const recIndex = state.rowsData.findIndex(
        (item: any) => item.id === currentKey,
      );
      actions.setNavigationIndex(recIndex);

      const selectedRow = state.rowsData.find(
        (item: any) => item.id === currentKey,
      );
      if (!selectedRow) {
        actions.setSelectedDataRow(null);
        actions.setSelectedKeys(new Set());
        clearSelectedMessageDetail();
        actions.setShowLoaderDetail(false);
        return;
      }
      actions.setSelectedDataRow(selectedRow);
      selectedMessageIdRef.current = String(selectedRow.id ?? "").trim();

      await loadSelectedMessageDetail(selectedRow);
      actions.setShowLoaderDetail(false);
      // Keep the list visible while bulk-select is in suppression mode so
      // the user can continue ticking rows without the detail pane flashing
      // in/out on every highlight change.
      if (bulkSelectedKeys.size < 2) {
        actions.setShowDetail(true);
      }

      if (selectedRow.seen === "0") {
        listMessageList.update(currentKey, { ...selectedRow, seen: "1" });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions, clearSelectedMessageDetail, loadSelectedMessageDetail, state.rowsData, state.selectedDataRow?.id, bulkSelectedKeys],
  );

  const Loader = () => (
    <Flex width="100%" justifyContent="center" marginTop={10}>
      <ProgressCircle aria-label="Loading…" isIndeterminate />
    </Flex>
  );

  const NoResultsMessage = () => (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      width="100%"
      UNSAFE_style={{ padding: "24px 16px", boxSizing: "border-box" }}
    >
      <IllustratedMessage>
        <NotFound />
        <Heading level={2}>
          {t("empty_message_list_title", {
            ns: "layout_components",
            defaultValue: "No messages yet",
          })}
        </Heading>
        <Content>
          {t("empty_message_list_description", {
            ns: "layout_components",
            defaultValue:
              "There are no messages in this folder. Use New email in the toolbar above to compose.",
          })}
        </Content>
      </IllustratedMessage>
    </Flex>
  );

  const nextNavigation = async () => {
    actions.setShowLoaderDetail(true);
    const nextMessageId = state.rowsData[state.navigationIndex + 1].id;

    const selectedRow = state.rowsData.find((item: any) => item.id === nextMessageId);
    if (selectedRow) {
      actions.setSelectedKeys(new Set([nextMessageId]));
      actions.setSelectedDataRow(selectedRow);
      selectedMessageIdRef.current = String(selectedRow.id ?? "").trim();
      await loadSelectedMessageDetail(selectedRow);
    }
    if (selectedRow) {
      actions.setNavigationIndex(state.navigationIndex + 1);
    }
    actions.setShowLoaderDetail(false);
  };

  const previousNavigation = async () => {
    if (state.navigationIndex <= 0) {
      return;
    }
    actions.setShowLoaderDetail(true);
    const prevMessageId = state.rowsData[state.navigationIndex - 1].id;

    const selectedRow = state.rowsData.find((item: any) => item.id === prevMessageId);
    if (selectedRow) {
      actions.setSelectedKeys(new Set([prevMessageId]));
      actions.setSelectedDataRow(selectedRow);
      selectedMessageIdRef.current = String(selectedRow.id ?? "").trim();
      await loadSelectedMessageDetail(selectedRow);
    }
    if (selectedRow) {
      actions.setNavigationIndex(state.navigationIndex - 1);
    }
    actions.setShowLoaderDetail(false);
  };

  const getNavigationPositionLabel = () => {
    const total = Number(state.totalMessageListRec) || 0;
    if (total <= 0) return "0/0";

    const current = Math.min(
      Math.max(state.navigationIndex + 1, 1),
      total,
    );

    return `${current}/${total}`;
  };

  const isMeetingMessage = state.messageContent?.type === "12";

  const getMessagePreviewUrl = useCallback(
    (messageId?: string) => {
      if (!messageId) return "";

      const previewBaseUrl = joinPaths(
        message_preview?.uri || "",
        "get_message",
      );

      const query = new URLSearchParams({
        server: String(store.Server ?? ""),
        session: String(store.SessionId ?? ""),
        message_id: String(messageId),
      });

      return `${previewBaseUrl}?${query.toString()}`;
    },
    [message_preview?.uri, store.Server, store.SessionId],
  );

  // Reply and Reply All functionality using NewMessageComponent
  const handleReplyClick = useCallback(async (replyType: "REPLY" | "REPLYALL") => {
    const targetMessageId = getDiscussionTargetMessageId(
      state.selectedDataRow,
      state.messageContent?.MESSAGE,
    );
    const targetMessageContent =
      targetMessageId &&
        targetMessageId !== String(state.selectedDataRow?.id || "")
        ? await getEmailContent({
          ...state.selectedDataRow,
          id: targetMessageId,
          seen: "1",
        })
        : state.messageContent;
    const detailMessage = targetMessageContent?.MESSAGE || {};
    const structuredRecipient =
      state.selectedDataRow?.RECIPIENT &&
        typeof state.selectedDataRow.RECIPIENT === "object"
        ? state.selectedDataRow.RECIPIENT
        : detailMessage.RECIPIENT &&
          typeof detailMessage.RECIPIENT === "object"
          ? detailMessage.RECIPIENT
          : state.selectedDataRow?.RECIPIENT || detailMessage.RECIPIENT;

    // Normalize reply payload so the composer can reliably resolve
    // sender/recipient data even when list and detail payloads differ.
    const messageForReply = {
      ...detailMessage,
      ...state.selectedDataRow,
      RECIPIENT: structuredRecipient,
      mailFrom: state.selectedDataRow?.mailFrom || detailMessage.mailFrom,
      sender: state.selectedDataRow?.sender || detailMessage.sender,
      senderContactId:
        state.selectedDataRow?.senderContactId || detailMessage.senderContactId,
      subject:
        state.selectedDataRow?.subject ||
        detailMessage.subject ||
        detailMessage.SUBJECT?.string,
      id: targetMessageId || state.selectedDataRow?.id,
      MESSAGE: targetMessageContent?.MESSAGE,
    };

    console.log("Communication reply payload", {
      replyType,
      selectedDataRow: state.selectedDataRow,
      detailMessage,
      messageForReply,
      recipient: messageForReply.RECIPIENT,
      mailFrom: messageForReply.mailFrom,
      sender: messageForReply.sender,
      senderContactId: messageForReply.senderContactId,
      subject: messageForReply.subject,
    });

    // Set up the NewMessageComponent with reply data
    actions.setReplyType(replyType);
    actions.setReplyMessageObj(messageForReply);
    actions.setReplyEmailAttachments(
      targetMessageContent?.processedAttachments ||
      state.messageContent?.processedAttachments ||
      [],
    );
    actions.setShowNewMessageDialog(true);
  }, [actions, getEmailContent, state.messageContent, state.selectedDataRow]);

  const handleForwardClick = useCallback(async () => {
    const targetMessageId = getDiscussionTargetMessageId(
      state.selectedDataRow,
      state.messageContent?.MESSAGE,
    );
    const targetMessageContent =
      targetMessageId &&
        targetMessageId !== String(state.selectedDataRow?.id || "")
        ? await getEmailContent({
          ...state.selectedDataRow,
          id: targetMessageId,
          seen: "1",
        })
        : state.messageContent;
    const detailMessage = targetMessageContent?.MESSAGE || {};
    const messageForForward = {
      ...detailMessage,
      ...state.selectedDataRow,
      id: targetMessageId || state.selectedDataRow?.id,
      MESSAGE: targetMessageContent?.MESSAGE,
    };

    actions.setReplyType("FORWARD");
    actions.setReplyMessageObj(messageForForward);
    actions.setReplyEmailAttachments(
      targetMessageContent?.processedAttachments ||
      state.messageContent?.processedAttachments ||
      (targetMessageContent?.MESSAGE?.ATTACHMENT
        ? Array.isArray(targetMessageContent.MESSAGE.ATTACHMENT)
          ? targetMessageContent.MESSAGE.ATTACHMENT
          : [targetMessageContent.MESSAGE.ATTACHMENT]
        : []),
    );
    actions.setShowNewMessageDialog(true);
  }, [actions, getEmailContent, state.messageContent, state.selectedDataRow]);

  const handleCommentClick = useCallback(async () => {
    if (!state.selectedDataRow?.id) {
      ToastQueue.negative(
        t("no_message_selected", {
          ns: "layout_components",
          defaultValue: "Please select a message first.",
        }),
        { timeout: 2500 },
      );
      return;
    }
    const targetMessageId = getDiscussionTargetMessageId(
      state.selectedDataRow,
      state.messageContent?.MESSAGE,
    );
    const targetMessageContent =
      targetMessageId &&
        targetMessageId !== String(state.selectedDataRow?.id || "")
        ? await getEmailContent({
          ...state.selectedDataRow,
          id: targetMessageId,
          seen: "1",
        })
        : state.messageContent;

    const detailMessage = targetMessageContent?.MESSAGE || {};
    const messageForComment = {
      // Start from list row for UI metadata, then overlay detail so context ids are not lost
      // (list rows often omit context fields; detail MESSAGE is authoritative).
      ...state.selectedDataRow,
      ...detailMessage,
      id: targetMessageId || state.selectedDataRow?.id,
      threadId:
        detailMessage?.threadId ??
        state.selectedDataRow?.threadId ??
        state.messageContent?.MESSAGE?.threadId,
      MESSAGE: targetMessageContent?.MESSAGE,
    };

    console.log("messageForComment", messageForComment);
    console.log("state?.messageContent?.MESSAGE", state);

    const resolvedCurrentContextId = resolveContextIdForMessage(state?.messageContent?.MESSAGE);
    setCommentChatCurrentContextId(resolvedCurrentContextId || undefined);
    setCommentChatTarget(messageForComment);
    setIsCommentChatOpen(true);
  }, [
    getEmailContent,
    state.messageContent,
    state.selectedDataRow,
    t,
  ]);

  useEffect(() => {
    if (!isCommentChatOpen || !state.selectedDataRow?.id) {
      return;
    }

    let cancelled = false;

    (async () => {
      const targetMessageId = getDiscussionTargetMessageId(
        state.selectedDataRow,
        state.messageContent?.MESSAGE,
      );
      const targetMessageContent =
        targetMessageId &&
          targetMessageId !== String(state.selectedDataRow?.id || "")
          ? await getEmailContent({
            ...state.selectedDataRow,
            id: targetMessageId,
            seen: "1",
          })
          : state.messageContent;
      const detailMessage = targetMessageContent?.MESSAGE || {};
      const nextCommentTarget = {
        ...state.selectedDataRow,
        ...detailMessage,
        id: targetMessageId || state.selectedDataRow?.id,
        threadId:
          detailMessage?.threadId ??
          state.selectedDataRow?.threadId ??
          state.messageContent?.MESSAGE?.threadId,
        MESSAGE: targetMessageContent?.MESSAGE,
      };

      if (!cancelled) {
        const resolvedCurrentContextId = resolveContextIdForMessage(nextCommentTarget);
        setCommentChatCurrentContextId(resolvedCurrentContextId || undefined);
        setCommentChatTarget(nextCommentTarget);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    getEmailContent,
    isCommentChatOpen,
    state.messageContent,
    state.selectedDataRow,
  ]);

  const openResolveOptionsScreen = useCallback(() => {
    setIsResolveOptionsOpen(true);
  }, []);

  const handleDeleteMessage = useCallback(async (messageId: string) => {
    const success = await deleteMessageOp(messageId);

    if (success) {
      // Remove the message from the async list
      listMessageList.remove(messageId);
      setBulkSelectedKeys((current) => {
        if (!current.has(String(messageId))) return current;
        const next = new Set(current);
        next.delete(String(messageId));
        return next;
      });

      // Get the next message in the list (if any) to show after deletion
      let nextMessageObj = getNextMessageAfterDeletion(
        state.rowsData,
        messageId,
      );

      // Update the rows data by filtering out the deleted message
      actions.setRowsData(
        state.rowsData.filter((item: any) => item.id !== messageId),
      );

      // Update the selected message to the next message in the list
      if (nextMessageObj) {
        const nextSelectedRow = state.rowsData.find(
          (item: any) => item.id === nextMessageObj.id,
        );
        if (nextSelectedRow) {
          actions.setSelectedKeys(new Set([nextSelectedRow.id]));
          actions.setSelectedDataRow(nextSelectedRow);
          selectedMessageIdRef.current = String(nextSelectedRow.id ?? "").trim();
          await loadSelectedMessageDetail(nextSelectedRow);
        }
      }

      // Show success notification
      ToastQueue.info(
        t("message_deleted_successfully", { ns: "layout_components" }),
        { timeout: 50 },
      );
    } else {
      // Show error notification if deletion failed
      ToastQueue.negative(
        t("error_deleting_message", { ns: "layout_components" }),
        { timeout: 50 },
      );
    }
  }, [actions, deleteMessageOp, listMessageList, loadSelectedMessageDetail, state.rowsData, t]);

  const handleBulkDeleteMessages = useCallback(async () => {
    const ids = Array.from(bulkSelectedKeys);
    if (ids.length === 0) return;

    const results = await Promise.all(
      ids.map(async (id) => ({
        id,
        success: await deleteMessageOp(id),
      })),
    );
    const deletedIds = results
      .filter((result) => result.success)
      .map((result) => result.id);
    const failedCount = results.length - deletedIds.length;

    if (deletedIds.length > 0) {
      const deletedSet = new Set(deletedIds);
      deletedIds.forEach((id) => listMessageList.remove(id));
      actions.setRowsData(
        state.rowsData.filter((item: any) => !deletedSet.has(String(item.id))),
      );
      setBulkSelectedKeys(new Set());

      if (deletedSet.has(String(state.selectedDataRow?.id ?? ""))) {
        actions.setSelectedDataRow({});
        actions.setSelectedKeys(new Set());
        clearSelectedMessageDetail();
      }

      refreshMessageCounts();
      ToastQueue.info(
        t("messages_deleted_successfully", {
          ns: "layout_components",
          defaultValue: `${deletedIds.length} messages deleted successfully`,
          count: deletedIds.length,
        }),
        { timeout: 50 },
      );
    }

    if (failedCount > 0) {
      ToastQueue.negative(
        t("error_deleting_messages", {
          ns: "layout_components",
          defaultValue: `${failedCount} messages could not be deleted`,
          count: failedCount,
        }),
        { timeout: 50 },
      );
    }
  }, [
    actions,
    bulkSelectedKeys,
    clearSelectedMessageDetail,
    deleteMessageOp,
    listMessageList,
    refreshMessageCounts,
    state.rowsData,
    state.selectedDataRow?.id,
    t,
  ]);

  const handleMarkDone = useCallback(async () => {
    if (!state.selectedDataRow?.id) {
      return;
    }

    await handleDeleteMessage(state.selectedDataRow.id);
  }, [handleDeleteMessage, state.selectedDataRow?.id]);

  const handleMoveMessage = useCallback(
    async (
      messageId: string,
      newNode: string | number,
      doNotMoveThread: string = "1",
    ): Promise<boolean> => {
      const success = await moveMessageOp(messageId, newNode, doNotMoveThread);

      if (success) {
        ToastQueue.info(
          t("message_moved_successfully", {
            ns: "layout_components",
            defaultValue: "Message moved successfully",
          }),
          { timeout: 50 },
        );
        reloadMessageList();
        refreshMessageCounts();
      } else {
        ToastQueue.negative(
          t("error_moving_message", {
            ns: "layout_components",
            defaultValue: "Error moving message",
          }),
          { timeout: 50 },
        );
      }

      return success;
    },
    [moveMessageOp, refreshMessageCounts, reloadMessageList, t],
  );

  const openMoveDialog = useCallback(() => {
    setIsMoveDialogOpen(true);
  }, []);

  const handleMobileSwipeAction = useCallback(
    (messageId: string, action: MobileSwipeAction) => {
      pendingMobileSwipeActionRef.current = { messageId, action };
      void handleListRowLargeClick({ currentKey: messageId });
    },
    [handleListRowLargeClick],
  );

  useEffect(() => {
    const pending = pendingMobileSwipeActionRef.current;
    if (!pending || state.showLoaderDetail) {
      return;
    }

    if (String(state.selectedDataRow?.id ?? "") !== pending.messageId) {
      return;
    }

    pendingMobileSwipeActionRef.current = null;

    if (pending.action === "reply" && showReplyAction) {
      void handleReplyClick("REPLY");
      return;
    }

    if (pending.action === "replyAll" && showReplyAction) {
      void handleReplyClick("REPLYALL");
      return;
    }

    if (pending.action === "forward" && showForwardAction) {
      void handleForwardClick();
      return;
    }

    if (pending.action === "comment" && showCommentAction) {
      void handleCommentClick();
      return;
    }

    if (pending.action === "move" && showMoveAction) {
      openMoveDialog();
    }
  }, [
    handleCommentClick,
    handleForwardClick,
    handleReplyClick,
    openMoveDialog,
    showCommentAction,
    showForwardAction,
    showMoveAction,
    showReplyAction,
    state.selectedDataRow?.id,
    state.showLoaderDetail,
  ]);

  const handleMoveSelectedMessage = useCallback(async (targetContextId: string) => {
    const selectedRowsForMove =
      bulkSelectedRows.length > 0 ? bulkSelectedRows : [state.selectedDataRow];
    const moveTargets = selectedRowsForMove
      .map((row: any) => ({
        id: String(row?.id ?? "").trim(),
        doNotMoveThread: String(row?.threadId ?? "").trim() ? "0" : "1",
      }))
      .filter((row) => row.id);

    if (moveTargets.length === 0 || !targetContextId) {
      ToastQueue.negative(
        t("select_context_first", {
          ns: "layout_components",
          defaultValue: "Please select a context first",
        }),
        { timeout: 50 },
      );
      return false;
    }

    const results = await Promise.all(
      moveTargets.map((target) =>
        moveMessageOp(target.id, targetContextId, target.doNotMoveThread),
      ),
    );
    const movedCount = results.filter(Boolean).length;
    const success = movedCount === moveTargets.length;

    if (success) {
      setIsMoveDialogOpen(false);
      setBulkSelectedKeys(new Set());
      reloadMessageList();
      refreshMessageCounts();
      ToastQueue.info(
        t("message_moved_successfully", {
          ns: "layout_components",
          defaultValue:
            movedCount > 1
              ? "Messages moved successfully"
              : "Message moved successfully",
        }),
        { timeout: 50 },
      );
    } else {
      ToastQueue.negative(
        t("error_moving_message", {
          ns: "layout_components",
          defaultValue: "Error moving message",
        }),
        { timeout: 50 },
      );
    }

    return success;
  }, [
    bulkSelectedRows,
    moveMessageOp,
    refreshMessageCounts,
    reloadMessageList,
    state.selectedDataRow,
    t,
  ]);

  const resizeMobileDetailIframe = useCallback((iframe: HTMLIFrameElement | null) => {
    if (!iframe) return;

    const doc = iframe.contentDocument;
    if (!doc) return;

    if (!doc.getElementById("mobile-email-normalize-style")) {
      const styleEl = doc.createElement("style");
      styleEl.id = "mobile-email-normalize-style";
      styleEl.textContent = `
        html, body {
          max-width: 100% !important;
          width: 100% !important;
          overflow-x: hidden !important;
          box-sizing: border-box !important;
          margin: 0 !important;
          padding: 0 !important;
          word-break: break-word !important;
          overflow-wrap: anywhere !important;
        }
        *, *::before, *::after {
          box-sizing: border-box !important;
          max-width: 100% !important;
        }
        img, table, pre, blockquote, div, p, span, td {
          max-width: 100% !important;
        }
        table {
          width: 100% !important;
          table-layout: fixed !important;
        }
        img {
          height: auto !important;
        }
        pre {
          white-space: pre-wrap !important;
          word-break: break-word !important;
          overflow-wrap: anywhere !important;
        }
      `;
      doc.head?.appendChild(styleEl);
    }

    const body = doc.body;
    const html = doc.documentElement;
    const nextHeight = Math.max(
      body?.scrollHeight || 0,
      body?.offsetHeight || 0,
      html?.scrollHeight || 0,
      html?.offsetHeight || 0,
      520,
    );

    setMobileDetailIframeHeight(nextHeight);
  }, []);

  const canUseDeleteShortcut =
    (!!state.selectedDataRow?.id || hasBulkSelection) &&
    messageType !== "trash" &&
    !state.showNewMessageDialog &&
    !isDeleteDialogOpen &&
    (showDeleteAction || hasBulkSelection);

  const openNewEmailComposer = useCallback(() => {
    actions.setReplyType("");
    actions.setReplyMessageObj(null);
    actions.setReplyEmailAttachments([]);
    actions.setComposeMessageType("");

    const messageListNodeId = normalizeAtollonNumericId(customTreeHandle) ?? null;

    if (messageListNodeId != null) {
      invokeNewMessageCreating(
        messageListNodeId,
        "message",
        undefined,
        undefined,
        true,
      );
      return;
    }

    actions.setShowNewMessageDialog(true);
  }, [actions, customTreeHandle]);

  useKeyboardShortcuts(
    [
      ...addKey("c", { ctrl: true, shift: true }, () => {
        if (!state.showNewMessageDialog) {
          openNewEmailComposer();
        }
      }),
    ],
    true,
  );

  useEffect(() => {
    const handleDeleteShortcut = (event: KeyboardEvent) => {
      if (!canUseDeleteShortcut || event.key !== "Delete") {
        return;
      }

      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable =
        !!target?.isContentEditable ||
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select";

      if (isEditable) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (hasBulkSelection) {
        handleBulkDeleteMessages();
        return;
      }

      if (shouldConfirmDelete) {
        setIsDeleteDialogOpen(true);
      } else {
        handleDeleteMessage(state.selectedDataRow.id);
      }
    };

    window.addEventListener("keydown", handleDeleteShortcut);
    return () => {
      window.removeEventListener("keydown", handleDeleteShortcut);
    };
  }, [
    canUseDeleteShortcut,
    handleBulkDeleteMessages,
    handleDeleteMessage,
    hasBulkSelection,
    shouldConfirmDelete,
    state.selectedDataRow?.id,
  ]);

  const MessageDialog = ({ close }: { close: () => void }) => (
    <Dialog size="S">
      <Flex UNSAFE_className={styles.delete_pop_up_parent}>
        <Content UNSAFE_className={styles.delete_pop_up_header}>
          {t("remove_message", { ns: "layout_components" })}
        </Content>
        <View UNSAFE_className={styles.delete_pop_up_line}></View>
        <View UNSAFE_className={styles.delete_pop_up_warning_text}>
          {t("are_you_sure_you_want_to_remove_message", {
            ns: "layout_components",
          })}
        </View>
        <View UNSAFE_className={styles.buttons_parent}>
          <View UNSAFE_className={styles.button_cancel}>
            <Link
              UNSAFE_className={styles.delete_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("no_keep_it", { ns: "layout_components" })}
            </Link>
          </View>
          <View UNSAFE_className={styles.button_confirm}>
            <div className={styles.trash_icon}>
              <TrashIcon size={32} />
            </div>
            <Link
              UNSAFE_className={styles.delete_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                handleDeleteMessage(state.selectedDataRow.id);
                close();
              }}
            >
              {t("yes_remove", { ns: "layout_components" })}
            </Link>
          </View>
        </View>
        <View UNSAFE_className={styles.ellipse_div}>
          <View UNSAFE_className={styles.delete_alert_icon}>
            <AlertIconFill size={72} />
          </View>
        </View>
      </Flex>
    </Dialog>
  );

  const RemoveMessageButton = () => (
    <ActionButton
      UNSAFE_className={styles.btn_delete}
      onPress={() => {
        if (hasBulkSelection) {
          handleBulkDeleteMessages();
          return;
        }

        if (!showDeleteAction || !state.selectedDataRow?.id) {
          return;
        }

        if (shouldConfirmDelete) {
          setIsDeleteDialogOpen(true);
        } else {
          handleDeleteMessage(state.selectedDataRow.id);
        }
      }}
    >
      <Trash className={styles.icon_trash} size={24} />
    </ActionButton>
  );

  const SearchDialog = ({ close }: { close: () => void }) => (
    <Dialog size="S" UNSAFE_className={styles.dialog_parent}>
      <Heading>
        <Flex alignItems="center" gap="size-100">
          <Text>{t("search", { ns: "layout_components" })}</Text>
        </Flex>
      </Heading>
      <Divider />
      <Content>
        <Form validationBehavior="native">
          <TextField
            autoFocus
            isRequired
            label={t("search", { ns: "layout_components" })}
            maxWidth="100%"
            onChange={(e) => {
              refs.searchedTextRef.current = e;
            }}
            validationState="invalid"
          />
        </Form>
      </Content>
      <Footer>
        <Flex
          direction={"row"}
          width={"100%"}
          alignContent={"center"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"size-200"}
        >
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            flexShrink={0}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.search_button_cancel}
          >
            <Link
              UNSAFE_className={styles.search_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("cancel", { ns: "layout_components" })}
            </Link>
          </Flex>
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.search_button_confirm}
          >
            <Link
              UNSAFE_className={styles.search_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                if (refs.searchedTextRef.current === "") {
                  return;
                }
                searchMessageList();
                refs.searchedTextRef.current = "";
                close();
              }}
            >
              {t("search", { ns: "layout_components" })}
            </Link>
          </Flex>
        </Flex>
      </Footer>
    </Dialog>
  );

  const SearchButton = () => (
    <DialogTrigger>
      <ActionButton UNSAFE_className={styles.no_border}>
        <SearchIcon size={24} />
      </ActionButton>
      {(close) => (
        <SearchDialog
          close={() => {
            close();
          }}
        />
      )}
    </DialogTrigger>
  );

  const FilterDialog = ({ close }: { close: () => void }) => (
    <Dialog size="M" UNSAFE_className={styles.dialog_parent}>
      <Heading>
        <Flex alignItems="center" gap="size-100">
          <Text>{t("Filter", { ns: "layout_components" })}</Text>
        </Flex>
      </Heading>
      <Divider />
      <Content>
        <Form>
          <Provider locale="en-US" UNSAFE_style={{ width: "100%" }}>
            <DatePicker
              width={"100%"}
              autoFocus
              id="dtCreatedFrom"
              label="Created from"
              onChange={(e) =>
                (refs.filterParamRef.current.createdFrom = e?.toString() ?? "")
              }
            />
          </Provider>
          <Provider locale="en-US">
            <DatePicker
              width={"100%"}
              label="Created to"
              onChange={(e) =>
                (refs.filterParamRef.current.createdTo = e?.toString() ?? "")
              }
            />
          </Provider>
          <TextField
            label="Sender"
            onChange={(e) => (refs.filterParamRef.current.sender = e)}
          />
          <TextField
            label="Subject"
            onChange={(e) => (refs.filterParamRef.current.subject = e)}
          />
          <Checkbox onChange={(e) => (refs.filterParamRef.current.newOnly = e)}>
            Only new
          </Checkbox>
          <Checkbox onChange={(e) => (refs.filterParamRef.current.starred = e)}>
            Only starred
          </Checkbox>
        </Form>
      </Content>
      <Footer>
        <Flex
          direction={"row"}
          width={"100%"}
          alignContent={"center"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"size-200"}
        >
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            flexShrink={0}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.filter_button_cancel}
          >
            <Link
              UNSAFE_className={styles.filter_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("cancel", { ns: "layout_components" })}
            </Link>
          </Flex>
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.filter_button_confirm}
          >
            <Link
              UNSAFE_className={styles.filter_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                filterMessageList();
                close();
              }}
            >
              {t("search", { ns: "layout_components" })}
            </Link>
          </Flex>
        </Flex>
      </Footer>
    </Dialog>
  );

  const FilterButton = () => (
    <DialogTrigger type="modal">
      <ActionButton UNSAFE_className={styles.no_border}>
        <FilterIcon UNSAFE_style={{ fill: "#b0bec5" }} />
      </ActionButton>
      {(close) => (
        <FilterDialog
          close={() => {
            close();
          }}
        />
      )}
    </DialogTrigger>
  );

  // TODO: Uncomment when menu action is needed
  // const menuAction = (key: string) => {
  //   if (key === "delete") {
  //     handleDeleteMessage(state.selectedDataRow.id);
  //   }
  // };

  const renderMessageList = () => {
    if (useMobileCompactLayout) {
      return state.showLoaderDetail ? (
        <View
          id="renderMessageList"
          position={"relative"}
          width={"100%"}
          height={"100%"}
          overflow={"hidden"}
          UNSAFE_className={styles.menu_messages_large_parent}
        >
          <Loader />
        </View>
      ) : (
        <View
          position={"relative"}
          width={"100%"}
          height={"100%"}
          overflow={"hidden"}
          UNSAFE_className={styles.mobile_message_list_shell}
        >
          <Flex
            direction={"row"}
            alignItems={"end"}
            justifyContent={"space-between"}
            UNSAFE_className={styles.mobile_message_tabs_row}
          >
            {messageTabs.map((tab) => (
              <Flex
                key={tab.key}
                direction={"column"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"size-100"}
                UNSAFE_className={styles.mobile_message_tab}
              >
                <Flex
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"6px"}
                >
                  <Text UNSAFE_className={styles.mobile_message_tab_label}>
                    {tab.label}
                  </Text>
                  <Flex
                    width={"24px"}
                    height={"24px"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    UNSAFE_style={{
                      backgroundColor: tab.color,
                      borderRadius: "999px",
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: 700,
                    }}
                  >
                    {tab.count > 999 ? "999+" : tab.count}
                  </Flex>
                </Flex>
                <View
                  UNSAFE_className={
                    tab.active
                      ? styles.mobile_message_tab_underline_active
                      : styles.mobile_message_tab_underline
                  }
                />
              </Flex>
            ))}
          </Flex>

          <View
            position={"absolute"}
            top={"58px"}
            left={"0px"}
            right={"0px"}
            bottom={"0px"}
            overflow={"auto"}
            UNSAFE_style={{ minHeight: 0 }}
          >
            <MessagesListComponent
              listId="listMessageCardsMobile"
              useMobileCard={true}
              listMessageList={listMessageList}
              rowsData={state.rowsData}
              handleListRowLargeClick={handleListRowLargeClick}
              totalMessageListRec={state.totalMessageListRec}
              selectedKeys={state.selectedKeys}
              isMobile={isMobile}
              isLoading={listMessageList.isLoading}
              items={listMessageList.items}
              selectedDataRow={state.selectedDataRow}
              messageType={messageType}
              onMobileRowOpen={(messageId) =>
                handleListRowLargeClick({ currentKey: messageId })
              }
              onMobileRowDelete={(messageId) => handleDeleteMessage(messageId)}
              onMobileRowReply={(messageId) => handleMobileSwipeAction(messageId, "reply")}
              onMobileRowReplyAll={(messageId) => handleMobileSwipeAction(messageId, "replyAll")}
              onMobileRowForward={(messageId) => handleMobileSwipeAction(messageId, "forward")}
              onMobileRowComment={(messageId) => handleMobileSwipeAction(messageId, "comment")}
              onMobileRowMove={(messageId) => handleMobileSwipeAction(messageId, "move")}
              bulkSelectedKeys={bulkSelectedKeys}
              onBulkSelectionChange={handleBulkSelectionChange}
            />
          </View>

          <ActionButton
            aria-label={t("new_email", {
              ns: "layout_components",
              defaultValue: "New email",
            })}
            onPress={openNewEmailComposer}
            UNSAFE_className={styles.mobile_fab_button}
          >
            <Plus size={26} />
          </ActionButton>
        </View>
      );
    }

    return state.showLoaderDetail ? (
      <View
        id="renderMessageList"
        position={"relative"}
        width={"100%"}
        height={"400px"}
        overflow={"hidden"}
        UNSAFE_className={styles.menu_messages_large_parent}
      >
        <Loader />
      </View>
    ) : (
      <View
        position={"relative"}
        width={"100%"}
        overflow={"hidden"}
        UNSAFE_className={styles.menu_messages_large_parent}
      >
        <View
          position={"absolute"}
          left={"0px"}
          width={"100%"}
          height={"100%"}
          overflow={"hidden"}
          UNSAFE_style={{
            fontSize: "12px",
            color: "#36474f",
            borderRadius: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Flex
            direction={"column"}
            position={"absolute"}
            top={"0px"}
            left={"0px"}
            width={"100%"}
            alignItems={"start"}
            justifyContent={"start"}
            maxHeight={"100vh"}
          >
            <MessagesListComponent
              listId="listMessageCards"
              useMobileCard={false}
              listMessageList={listMessageList}
              rowsData={state.rowsData}
              handleListRowLargeClick={handleListRowLargeClick}
              totalMessageListRec={state.totalMessageListRec}
              selectedKeys={state.selectedKeys}
              isMobile={isMobile}
              isLoading={listMessageList.isLoading}
              items={listMessageList.items}
              selectedDataRow={state.selectedDataRow}
              messageType={messageType}
              onDesktopRowDragStart={onDesktopRowDragStart}
              bulkSelectedKeys={bulkSelectedKeys}
              onBulkSelectionChange={handleBulkSelectionChange}
            />
          </Flex>
        </View>
      </View>
    );
  };

  const renderMessageListDetailLarge = () => {
    if (isBulkDetailSuppressed) {
      return (
        <View
          width={"100%"}
          position={"relative"}
          height={"100%"}
          overflow={"hidden"}
          UNSAFE_className={styles.message_list_detail_wrapper_large}
        >
          <BulkSelectionPlaceholder
            count={bulkSelectedCount}
            styles={styles}
            label={t("messages_selected", {
              ns: "layout_components",
              count: bulkSelectedCount,
              defaultValue: "messages selected",
            })}
          />
        </View>
      );
    }
    return (
      <View
        width={"100%"}
        position={"relative"}
        height={"100%"}
        overflow={"hidden"}
        UNSAFE_className={styles.message_list_detail_wrapper_large}
      >
        <Flex
          direction={"column"}
          position={"absolute"}
          top={0}
          left={0}
          width={"100%"}
          height={"98%"}
          alignItems={"start"}
          justifyContent={"start"}
          UNSAFE_className={styles.message_list_detail_parent}
        >
          <Flex
            direction={"column"}
            height={"98%"}
            alignSelf={"stretch"}
            alignItems={"center"}
            justifyContent={"start"}
          >
            <View
              position={"relative"}
              top={0}
              right={0}
              width={"100%"}
              height={"64px"}
              overflow={"hidden"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"24px"}
                left={"10px"}
                justifyContent={"start"}
                gap={"18px"}
                alignItems={"center"}
              >
                <Flex
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"12px"}
                >
                  <View marginStart={"5px"} marginEnd={"3px"}>
                    <ArrowLeft
                      size={24}
                      className={styles.message_list_header_icon}
                      onClick={() => {
                        actions.setShowDetail(false);
                      }}
                    />
                  </View>
                  {showReplyAction && (
                    <Flex
                      direction={"row"}
                      width={"34px"}
                      height={"34px"}
                      flexShrink={0}
                      justifyContent={"center"}
                      UNSAFE_className={styles.action_button}
                    >
                      <ActionButton
                        UNSAFE_style={{
                          background: "transparent",
                          border: "none",
                        }}
                        onPress={() => handleReplyClick("REPLY")}
                        aria-label="Reply"
                      >
                        <ReplyIcon size={24} />
                      </ActionButton>
                    </Flex>
                  )}
                  {showReplyAction && (
                    <Flex
                      direction={"row"}
                      width={"34px"}
                      height={"34px"}
                      flexShrink={0}
                      justifyContent={"center"}
                      UNSAFE_className={styles.action_button}
                    >
                      <ActionButton
                        UNSAFE_style={{
                          background: "transparent",
                          border: "none",
                        }}
                        onPress={() => handleReplyClick("REPLYALL")}
                        aria-label="Reply All"
                      >
                        <ReplyAllIcon size={24} />
                      </ActionButton>
                    </Flex>
                  )}
                  {showForwardAction && (
                    <Flex
                      direction={"row"}
                      width={"34px"}
                      height={"34px"}
                      flexShrink={0}
                      justifyContent={"center"}
                      UNSAFE_className={styles.action_button}
                    >
                      <ActionButton
                        UNSAFE_style={{
                          background: "transparent",
                          border: "none",
                        }}
                        onPress={() => handleForwardClick()}
                        aria-label="Forward/New Message"
                      >
                        <ArrowForwardIcon size={24} />
                      </ActionButton>
                    </Flex>
                  )}
                </Flex>
                {showCommentAction && (
                  <>
                    {(showReplyAction || showForwardAction) && (
                      <View
                        position={"relative"}
                        width={"1px"}
                        height={"24px"}
                        UNSAFE_className={styles.message_list_detail_header_divider}
                      ></View>
                    )}
                    <Flex
                      direction={"row"}
                      height={"32px"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      width={"100px"}
                      UNSAFE_className={styles.btn_comment}
                    >
                      <button
                        className={styles.btn_comment_text}
                        style={{
                          background: "transparent",
                          border: "none",
                          width: "100%",
                          fontSize: "14px",
                        }}
                        onClick={() => handleCommentClick()}
                      >
                        {t("comment", { ns: "layout_components" })}
                      </button>
                    </Flex>
                  </>
                )}
              </Flex>
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"27px"}
                right={"20px"}
                alignItems={"center"}
                justifyContent={"start"}
                gap={"10px"}
              >
                {showDeleteAction && <RemoveMessageButton />}
                <MoreIcon size={24} />
              </Flex>
            </View>
            {(showResolveAction || showMarkDoneAction) && (
              <Flex
                direction={"row"}
                width={"100%"}
                alignItems={"center"}
                justifyContent={"start"}
                UNSAFE_style={{
                  padding: "8px 16px 0 16px",
                  flexShrink: 0,
                }}
              >
                <button
                  type="button"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    minHeight: "36px",
                    padding: "0 14px",
                    borderRadius: "999px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    border: showMarkDoneAction
                      ? "1px solid #1976d2"
                      : "1px solid #ff9800",
                    color: showMarkDoneAction ? "#1976d2" : "#ff9800",
                    background: showMarkDoneAction ? "#f5f9ff" : "#fff8ef",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => {
                    if (showMarkDoneAction) {
                      handleMarkDone();
                      return;
                    }

                    openResolveOptionsScreen();
                  }}
                >
                  {showMarkDoneAction ? (
                    <Check2Circle size={16} />
                  ) : (
                    <QuestionCircle size={16} />
                  )}
                  <span>
                    {showMarkDoneAction
                      ? t("mark_as_done", {
                        ns: "layout_components",
                        defaultValue: "Mark as done",
                      })
                      : t("Resolve message", {
                        ns: "layout_components",
                        defaultValue: "Resolve message",
                      })}
                  </span>
                </button>
              </Flex>
            )}
            <Flex
              direction={"column"}
              width={"100%"}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                alignItems={"start"}
                justifyContent={"start"}
                gap={"18px"}
                UNSAFE_style={{
                  borderBottom: "1px solid #eceff1",
                  paddingLeft: "13px",
                  paddingBottom: "5px",
                }}
              >
                <View UNSAFE_className={styles.user_img}>
                  <img
                    id="img_renderMessageListDetailLarge"
                    className={styles.avatar_desktop_light}
                    alt=""
                    src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                  />
                </View>
                <Flex
                  direction={"column"}
                  width={"100%"}
                  alignItems={"start"}
                  justifyContent={"start"}
                  gap={"5px"}
                >
                  {messageType !== "concept" && (
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      alignItems={"end"}
                      justifyContent={"space-between"}
                    >
                      <Flex
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"start"}
                        gap={"8px"}
                      >
                        <View UNSAFE_className={styles.user_full_name}>
                          {utils.getSenderName(state.selectedDataRow.sender)}
                        </View>
                        <View
                          width={"1px"}
                          position={"relative"}
                          height={"12px"}
                          UNSAFE_className={styles.user_detail_divider}
                        ></View>
                        <Flex
                          direction={"row"}
                          alignItems={"center"}
                          gap={"size-50"}
                        >
                          <View UNSAFE_className={styles.user_email}>
                            {utils.getSenderEmail(state.selectedDataRow.mailFrom)}
                          </View>
                          <EmailSenderContactBadge
                            messageRow={senderContactBadgeRow}
                            mailFrom={state.selectedDataRow.mailFrom}
                            sender={state.selectedDataRow.sender ?? ""}
                            enabled={Boolean(state.selectedDataRow?.mailFrom)}
                          />
                        </Flex>
                      </Flex>
                      <View
                        position={"absolute"}
                        right={"25px"}
                        UNSAFE_className={styles.div_date}
                      >
                        {moment(state.selectedDataRow.date).format(
                          "DD.MM.YYYY HH:mm",
                        )}
                      </View>
                    </Flex>
                  )}
                  <Flex
                    direction={"column"}
                    alignSelf={"stretch"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"8px"}
                    UNSAFE_className={styles.subject_parent}
                  >
                    <View
                      position={"relative"}
                      alignSelf={"stretch"}
                      UNSAFE_className={styles.email_subject}
                    >
                      {state.selectedDataRow.subject}
                    </View>
                    <RecipientToField
                      recipient={resolveRecipientForDisplay(
                        state.selectedDataRow?.id,
                        state.selectedDataRow?.RECIPIENT,
                        state.messageContent?.MESSAGE,
                      )}
                    />
                    <ContextPathLinks
                      message={state.messageContent.MESSAGE}
                      styles={styles}
                    />
                    <RecipientNotifyField
                      recipient={resolveNotifyRecipientForDisplay(
                        state.selectedDataRow?.id,
                        state.messageContent?.MESSAGE,
                      )}
                    />
                    {state.messageContent.participantsName &&
                      state.messageContent.type === "12" && (
                        <Flex
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          gap={"6px"}
                        >
                          <View UNSAFE_className={styles.address}>
                            {t("participants", {
                              ns: "layout_components",
                              defaultValue: "Participants",
                            })}
                            :&nbsp;
                            {state.messageContent.participantsName}
                          </View>
                        </Flex>
                      )}
                    <MessageAttachment
                      store={store}
                      styles={styles}
                      messageContent={state.messageContent}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <View
              width={"93%"}
              position={"relative"}
              UNSAFE_className={styles.email_content}
              height={"75%"}
            >
              {/* <div
                dangerouslySetInnerHTML={{
                  __html: messageType != 'concept' ? utils.oldEmailContentFix(state.messageContent.MESSAGE?.PREVIEW?.content) : state.messageContent.messageTemplate?.BODY?.string,
                }}
              /> */}
              <iframe
                {...(messageType !== "concept"
                  ? { src: getMessagePreviewUrl(state.selectedDataRow?.id) }
                  : {
                    srcDoc:
                      state.messageContent.messageTemplate?.BODY?.string,
                  })}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  backgroundColor: "#fff",
                  paddingLeft: "16px",
                }}
                title="Email Content"
                sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
              />
            </View>
          </Flex>
        </Flex>
        <Flex
          direction={"column"}
          position={"absolute"}
          bottom={"16px"}
          left={"calc(50% - 128.5px)"}
          alignItems={"center"}
          justifyContent={"end"}
        >
          <Flex
            direction={"row"}
            width={"258px"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={"32px"}
          >
            {state.navigationIndex > 0 && (
              <Flex
                direction={"row"}
                height={"32px"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"8px"}
                UNSAFE_className={styles.btn_previous}
              >
                <Button
                  variant="primary"
                  onPress={previousNavigation}
                  UNSAFE_style={{
                    background: "transparent",
                    border: "none",
                    width: "100%",
                  }}
                >
                  <>
                    <ArrowLeft className={styles.icon_navigate} />
                    &nbsp;&nbsp;&nbsp;
                    <View UNSAFE_className={styles.btn_text}>
                      {t("previous", { ns: "layout_components" })}
                    </View>
                  </>
                </Button>
              </Flex>
            )}
            {state.navigationIndex < state.rowsData.length - 1 &&
              state.rowsData.length > 0 && (
                <Flex
                  direction={"row"}
                  width={"113px"}
                  height={"32px"}
                  flexShrink={0}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"8px"}
                  UNSAFE_className={styles.btn_next}
                >
                  <Button
                    id="btn_next_large_detail"
                    variant="primary"
                    onPress={nextNavigation}
                    UNSAFE_style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                    }}
                  >
                    <>
                      <View UNSAFE_className={styles.btn_text}>
                        {t("next", { ns: "layout_components" })}
                      </View>
                      &nbsp;&nbsp;&nbsp;
                      <ArrowRight className={styles.icon_navigate} />
                    </>
                  </Button>
                </Flex>
              )}
          </Flex>
        </Flex>
      </View>
    );
  };

  const renderMessageListDetailMobile = () => {
    if (isBulkDetailSuppressed) {
      return (
        <div style={{ width: "100%", height: "100vh" }}>
          <View
            width={"100%"}
            top={compactDetailTopOffset}
            position={"relative"}
            height={
              compactDetailTopOffset > 0
                ? `calc(100vh - ${compactDetailTopOffset}px)`
                : "100vh"
            }
            overflow={"hidden"}
            UNSAFE_className={styles.message_list_detail_wrapper_large}
          >
            <BulkSelectionPlaceholder
              count={bulkSelectedCount}
              styles={styles}
              label={t("messages_selected", {
                ns: "layout_components",
                count: bulkSelectedCount,
                defaultValue: "messages selected",
              })}
            />
          </View>
        </div>
      );
    }
    return (
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ width: "100%", height: "100vh" }}
      >
        <View
          width={"100%"}
          top={compactDetailTopOffset}
          position={"relative"}
          height={
            compactDetailTopOffset > 0
              ? `calc(100vh - ${compactDetailTopOffset}px)`
              : "100vh"
          }
          overflow={"hidden"}
          UNSAFE_className={styles.message_list_detail_wrapper_large}
        >
          <Flex
            direction={"column"}
            position={"relative"}
            top={compactDetailTopOffset}
            width={"100%"}
            height={"100%"}
            alignItems={"start"}
            justifyContent={"start"}
            UNSAFE_className={styles.message_list_detail_parent}
          >
            <Flex
              direction={"column"}
              height={"100%"}
              alignSelf={"stretch"}
              alignItems={"center"}
              justifyContent={"start"}
            >
              <View
                position={"relative"}
                top={0}
                right={0}
                width={"100%"}
                height={"48px"}
                overflow={"hidden"}
                UNSAFE_className={styles.email_content_header_parent_large}
              >
                <Flex
                  direction={"row"}
                  position={"absolute"}
                  top={"10px"}
                  left={"10px"}
                  justifyContent={"start"}
                  gap={"18px"}
                  alignItems={"center"}
                >
                  <Flex
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"12px"}
                  >
                    <Flex
                      marginStart={"5px"}
                      marginEnd={"3px"}
                      UNSAFE_style={{
                        color: "#8f9fa6",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      <i
                        className={`bi bi-chevron-left`}
                        onClick={() => {
                          setShowMobileMenu?.(true);
                          actions.setShowDetail(false);
                        }}
                      >
                        <View
                          marginStart={"10px"}
                          UNSAFE_style={{ display: "inline" }}
                        >
                          Back
                        </View>
                      </i>
                    </Flex>
                    {/* <Flex direction={'row'} width={'34px'} height={'34px'} flexShrink={0} justifyContent={'center'} UNSAFE_className={styles.action_button}>
                    <ReplyIcon size={24} />
                  </Flex>
                  <Flex direction={'row'} width={'34px'} height={'34px'} flexShrink={0} justifyContent={'center'} UNSAFE_className={styles.action_button}>
                    <ReplyAllIcon size={24} />
                  </Flex>
                  <Flex direction={'row'} width={'34px'} height={'34px'} flexShrink={0} justifyContent={'center'} UNSAFE_className={styles.action_button}>
                    <ArrowForwardIcon size={24} />
                  </Flex> */}
                  </Flex>
                  {/* <View position={'relative'} width={'1px'} height={'24px'} UNSAFE_className={styles.message_list_detail_header_divider}></View>
                <Flex direction={'row'} height={'32px'} alignItems={'center'} justifyContent={'center'} width={'100px'} UNSAFE_className={styles.btn_comment}>
                  <button className={styles.btn_comment_text} style={{ background: 'transparent', border: 'none', width: '100%', fontSize: '14px' }}>
                    {t('comment', { ns: 'layout_components' })}
                  </button>
                </Flex> */}
                </Flex>

                <Flex
                  direction={"row"}
                  position={"absolute"}
                  top={"10px"}
                  right={"10px"}
                  alignItems={"center"}
                  justifyContent={"start"}
                >
                  <View
                    position={"relative"}
                    UNSAFE_className={styles.div_date}
                  >
                    {moment(state.selectedDataRow.date).format(
                      "DD.MM.YYYY HH:mm",
                    )}
                  </View>
                </Flex>
                {/* <Flex direction={'row'} position={'absolute'} top={'32px'} right={'2px'} alignItems={'center'} justifyContent={'start'}>
                <MenuTrigger>
                  <Link>
                    <span aria-label="Open context menu" role="button">
                      <i className="bi bi-three-dots fs-7"></i>
                    </span>
                  </Link>
                  <Menu width={'100%'} onAction={(key: any) => menuAction(key)}>
                    <Item key="delete" textValue="delete">
                      <i className="bi bi-trash" style={iconStyle}></i>&nbsp;&nbsp;
                      <span style={{ color: '#36474F', paddingLeft: '5px', paddingTop: '4px' }}>{t('remove', { ns: 'layout_components' })}</span>
                    </Item>
                  </Menu>
                </MenuTrigger>
              </Flex> */}
              </View>
              <Flex
                direction={"column"}
                width={"100%"}
                alignItems={"start"}
                justifyContent={"start"}
                UNSAFE_className={styles.email_content_header_parent_large}
              >
                <Flex
                  direction={"row"}
                  alignSelf={"stretch"}
                  alignItems={"start"}
                  justifyContent={"start"}
                  gap={"18px"}
                  UNSAFE_style={{
                    borderBottom: "1px solid #eceff1",
                    paddingLeft: "13px",
                    paddingBottom: "15px",
                  }}
                >
                  <View UNSAFE_className={styles.user_img}>
                    <img
                      id="img_renderMessageListDetailMobile"
                      className={styles.avatar_desktop_light}
                      alt=""
                      src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                    />
                  </View>

                  <Flex
                    direction={"column"}
                    width={"100%"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"5px"}
                  >
                    {messageType !== "concept" && (
                      <Flex
                        direction={"row"}
                        alignSelf={"stretch"}
                        alignItems={"end"}
                        justifyContent={"space-between"}
                      >
                        <Flex
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"start"}
                          gap={"8px"}
                        >
                          <View UNSAFE_className={styles.user_full_name}>
                            {utils.getSenderName(state.selectedDataRow.sender)}
                          </View>
                          <View
                            width={"1px"}
                            position={"relative"}
                            height={"12px"}
                            UNSAFE_className={styles.user_detail_divider}
                          ></View>
                          <Flex
                            direction={"row"}
                            alignItems={"center"}
                            gap={"size-50"}
                          >
                            <View UNSAFE_className={styles.user_email}>
                              {utils.getSenderEmail(
                                state.selectedDataRow.mailFrom,
                              )}
                            </View>
                            <EmailSenderContactBadge
                              messageRow={senderContactBadgeRow}
                              mailFrom={state.selectedDataRow.mailFrom}
                              sender={state.selectedDataRow.sender ?? ""}
                              enabled={Boolean(state.selectedDataRow?.mailFrom)}
                            />
                          </Flex>
                        </Flex>
                      </Flex>
                    )}
                    <Flex
                      direction={"column"}
                      alignSelf={"stretch"}
                      alignItems={"start"}
                      justifyContent={"start"}
                      gap={"8px"}
                      UNSAFE_className={styles.subject_parent}
                    >
                      <View
                        position={"relative"}
                        alignSelf={"stretch"}
                        UNSAFE_className={styles.email_subject}
                      >
                        {state.selectedDataRow.subject}
                      </View>
                      <RecipientToField
                        recipient={resolveRecipientForDisplay(
                          state.selectedDataRow?.id,
                          state.selectedDataRow?.RECIPIENT,
                          state.messageContent?.MESSAGE,
                        )}
                      />
                      <ContextPathLinks
                        message={state.messageContent.MESSAGE}
                        styles={styles}
                      />
                      <RecipientNotifyField
                        recipient={resolveNotifyRecipientForDisplay(
                          state.selectedDataRow?.id,
                          state.messageContent?.MESSAGE,
                        )}
                      />
                      {state.messageContent.participantsName &&
                        isMeetingMessage && (
                          <Flex
                            direction={"row"}
                            alignItems={"center"}
                            justifyContent={"center"}
                            gap={"6px"}
                          >
                            <View UNSAFE_className={styles.address}>
                              {t("participants", {
                                ns: "layout_components",
                                defaultValue: "Participants",
                              })}
                              :{" "}
                              {state.messageContent.participantsName}
                            </View>
                          </Flex>
                        )}
                      <MessageAttachment
                        store={store}
                        styles={styles}
                        messageContent={state.messageContent}
                      />
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              <View
                width={"100%"}
                position={"relative"}
                UNSAFE_className={styles.email_content}
                height={"55%"}
                UNSAFE_style={{ backgroundColor: "#fff", padding: "5px" }}
              >
                {/* <div
                  dangerouslySetInnerHTML={{
                    __html: messageType != 'concept' ? utils.oldEmailContentFix(state.messageContent.MESSAGE?.PREVIEW?.content) : state.messageContent.messageTemplate?.BODY?.string,
                  }}
                /> */}

                <iframe
                  {...(messageType !== "concept"
                    ? { src: getMessagePreviewUrl(state.selectedDataRow?.id) }
                    : {
                      srcDoc:
                        state.messageContent.messageTemplate?.BODY?.string,
                    })}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    backgroundColor: "#fff",
                  }}
                  title="Email Content"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
                />
              </View>
            </Flex>
          </Flex>
          <Flex
            direction={"column"}
            position={"absolute"}
            bottom={"80px"}
            left={"calc(50% - 188px)"}
            alignItems={"center"}
            justifyContent={"end"}
          >
            <Flex
              direction={"row"}
              width={"360px"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={"32px"}
            >
              {state.navigationIndex > 0 && (
                <Flex
                  direction={"row"}
                  height={"32px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"8px"}
                  UNSAFE_className={styles.btn_previous}
                >
                  <Button
                    variant="primary"
                    onPress={previousNavigation}
                    UNSAFE_style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                    }}
                  >
                    <>
                      <ArrowLeft className={styles.icon_navigate} />
                      &nbsp;&nbsp;&nbsp;
                      <View UNSAFE_className={styles.btn_text}>
                        {t("previous", { ns: "layout_components" })}
                      </View>
                    </>
                  </Button>
                </Flex>
              )}
              <Flex
                direction={"row"}
                height={"18px"}
                flexShrink={0}
                alignItems={"center"}
                justifyContent={"center"}
                UNSAFE_style={{
                  fontWeight: "bold",
                  overflow: "hidden",
                  padding: "4px 0px",
                  boxSizing: "border-box",
                  textAlign: "center",
                  fontSize: "14px",
                  color: "#1976d2",
                }}
              >
                <div key={state.navigationIndex}>{getNavigationPositionLabel()}</div>
              </Flex>
              {state.navigationIndex < state.rowsData.length - 1 &&
                state.rowsData.length > 0 && (
                  <Flex
                    direction={"row"}
                    width={"113px"}
                    height={"32px"}
                    flexShrink={0}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"8px"}
                    UNSAFE_className={styles.btn_next}
                  >
                    <Button
                      id="btn_next_large_mobile"
                      variant="primary"
                      onPress={nextNavigation}
                      UNSAFE_style={{
                        background: "transparent",
                        border: "none",
                        width: "100%",
                      }}
                    >
                      <>
                        <View UNSAFE_className={styles.btn_text}>
                          {t("next", { ns: "layout_components" })}
                        </View>
                        &nbsp;&nbsp;&nbsp;
                        <ArrowRight className={styles.icon_navigate} />
                      </>
                    </Button>
                  </Flex>
                )}
            </Flex>
          </Flex>
          <div className={styles.action_button_parent}>
            <div className={styles.action_button_group}>
              {showReplyAction && (
                <div
                  className={styles.action_icon_parent}
                  onClick={() => handleReplyClick("REPLY")}
                >
                  <div className={styles.iconReplyParent}>
                    <ReplyIcon size={24} />
                    <div
                      className={styles.action_icon_text}
                      style={{ paddingTop: "5px" }}
                    >
                      Reply
                    </div>
                  </div>
                </div>
              )}
              {showReplyAction && (
                <div
                  className={styles.action_icon_parent}
                  onClick={() => handleReplyClick("REPLYALL")}
                >
                  <div className={styles.iconReplyAllParent}>
                    <ReplyAllIcon size={24} />
                    <div
                      className={styles.action_icon_text}
                      style={{ paddingTop: "5px" }}
                    >
                      Reply All
                    </div>
                  </div>
                </div>
              )}
              {showForwardAction && (
                <div
                  className={styles.action_icon_parent}
                  onClick={() => handleForwardClick()}
                >
                  <div className={styles.iconArrowForwardParent}>
                    <ArrowForwardIcon size={24} />
                    <div
                      className={styles.action_icon_text}
                      style={{ paddingTop: "3px" }}
                    >
                      Forward
                    </div>
                  </div>
                </div>
              )}
              <div className={styles.action_icon_parent}>
                <div className={styles.iconAiChatParent}>
                  <AIChatIconColored size={24} height={16} />
                  <div
                    className={styles.action_icon_text}
                    style={{ paddingTop: "5px" }}
                  >
                    AI chat
                  </div>
                </div>
              </div>
              {showCommentAction && (
                <div
                  className={styles.action_icon_parent}
                  onClick={() => handleCommentClick()}
                >
                  <div className={styles.iconCommentParent}>
                    <CommentIcon size={16} height={16} color="#1976D2" />
                    <div
                      className={styles.action_icon_text}
                      style={{ paddingTop: "5px" }}
                    >
                      Comment
                    </div>
                  </div>
                </div>
              )}
              <div className={styles.action_icon_parent}>
                <div className={styles.iconMoreParent}>
                  <MoreIcon size={24} />
                  <div
                    className={styles.action_icon_text}
                    style={{ paddingTop: "4px" }}
                  >
                    More
                  </div>
                </div>
              </div>
            </div>
          </div>
        </View>
      </div>
    );
  };

  const renderMessageListDetailPhone = () => {
    if (isBulkDetailSuppressed) {
      return (
        <View
          width={"100%"}
          height={"100%"}
          position={"relative"}
          UNSAFE_className={styles.mobile_detail_shell}
        >
          <BulkSelectionPlaceholder
            count={bulkSelectedCount}
            styles={styles}
            label={t("messages_selected", {
              ns: "layout_components",
              count: bulkSelectedCount,
              defaultValue: "messages selected",
            })}
          />
        </View>
      );
    }
    return (
      <View
        width={"100%"}
        height={"100%"}
        position={"relative"}
        UNSAFE_className={styles.mobile_detail_shell}
      >
        <View UNSAFE_className={styles.mobile_detail_scroll}>
          <Flex
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            UNSAFE_className={styles.mobile_detail_topbar}
          >
            <button
              type="button"
              className={styles.mobile_detail_back}
              onClick={() => {
                setShowMobileMenu?.(true);
                actions.setShowDetail(false);
              }}
            >
              <ChevronLeft size={18} />
              <span>{t("Back", { ns: "layout_components", defaultValue: "Back" })}</span>
            </button>
            <View UNSAFE_className={styles.mobile_detail_date}>
              {moment(state.selectedDataRow.date).format("DD.MM.YYYY HH:mm")}
            </View>
          </Flex>

          <Flex
            direction={"column"}
            alignItems={"stretch"}
            justifyContent={"start"}
            UNSAFE_className={styles.mobile_detail_header_card}
          >
            <Flex direction={"row"} gap={"12px"} alignItems={"start"}>
              <View UNSAFE_className={styles.user_img}>
                <img
                  id="img_renderMessageListDetailPhone"
                  className={styles.avatar_desktop_light}
                  alt=""
                  src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                />
              </View>
              <Flex direction={"column"} flex={1} gap={"4px"}>
                {messageType !== "concept" && (
                  <Flex
                    direction={"column"}
                    alignItems={"start"}
                    gap={"2px"}
                    UNSAFE_className={styles.mobile_detail_sender_row}
                  >
                    <View UNSAFE_className={styles.user_full_name}>
                      {utils.getSenderName(state.selectedDataRow.sender)}
                    </View>
                    <Flex
                      direction={"row"}
                      alignItems={"center"}
                      gap={"size-50"}
                      UNSAFE_style={{ flexWrap: "wrap" }}
                    >
                      <View UNSAFE_className={styles.user_email}>
                        {utils.getSenderEmail(state.selectedDataRow.mailFrom)}
                      </View>
                      <EmailSenderContactBadge
                        messageRow={senderContactBadgeRow}
                        mailFrom={state.selectedDataRow.mailFrom}
                        sender={state.selectedDataRow.sender ?? ""}
                        enabled={Boolean(state.selectedDataRow?.mailFrom)}
                      />
                    </Flex>
                  </Flex>
                )}
              </Flex>
            </Flex>

            <Flex
              direction={"column"}
              alignItems={"stretch"}
              justifyContent={"start"}
              gap={"size-150"}
              UNSAFE_className={styles.mobile_detail_meta_container}
            >
              {state.selectedDataRow?.subject && (
                <View UNSAFE_className={styles.mobile_detail_subject}>
                  {state.selectedDataRow.subject}
                </View>
              )}

              <RecipientDetailField
                omitNotify
                recipient={resolveRecipientForDisplay(
                  state.selectedDataRow?.id,
                  state.selectedDataRow?.RECIPIENT,
                  state.messageContent?.MESSAGE,
                )}
              />

              <View UNSAFE_className={styles.mobile_detail_meta_section}>
                <ContextPathLinks
                  message={state.messageContent.MESSAGE}
                  styles={styles}
                />
              </View>
              <RecipientNotifyField
                recipient={resolveNotifyRecipientForDisplay(
                  state.selectedDataRow?.id,
                  state.messageContent?.MESSAGE,
                )}
              />

              {state.messageContent.participantsName &&
                isMeetingMessage && (
                  <Flex
                    direction={"row"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"6px"}
                    UNSAFE_className={styles.mobile_detail_meta_section}
                  >
                    <View UNSAFE_className={styles.mobile_detail_meta_label}>
                      {t("participants", {
                        ns: "layout_components",
                        defaultValue: "Participants",
                      })}
                      :
                    </View>
                    <View UNSAFE_className={styles.mobile_detail_meta_value}>
                      {state.messageContent.participantsName}
                    </View>
                  </Flex>
                )}

              <View UNSAFE_className={styles.mobile_detail_attachment_section}>
                <MessageAttachment
                  store={store}
                  styles={styles}
                  messageContent={state.messageContent}
                />
              </View>

              {(showResolveAction || showMarkDoneAction) && (
                <ActionButton
                  UNSAFE_className={
                    showMarkDoneAction
                      ? styles.mobile_mark_done_button
                      : styles.mobile_resolve_button
                  }
                  aria-label={
                    showMarkDoneAction
                      ? t("mark_as_done", {
                        ns: "layout_components",
                        defaultValue: "Mark as done",
                      })
                      : t("Resolve message", {
                        ns: "layout_components",
                        defaultValue: "Resolve message",
                      })
                  }
                  onPress={() => {
                    if (showMarkDoneAction) {
                      handleMarkDone();
                      return;
                    }

                    openResolveOptionsScreen();
                  }}
                >
                  <Flex
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    width={"100%"}
                  >
                    <Flex direction={"row"} alignItems={"center"} gap={"8px"}>
                      {showMarkDoneAction ? (
                        <Check2Circle size={18} />
                      ) : (
                        <QuestionCircle size={18} />
                      )}
                      <Text>
                        {showMarkDoneAction
                          ? t("mark_as_done", {
                            ns: "layout_components",
                            defaultValue: "Mark as done",
                          })
                          : t("Resolve message", {
                            ns: "layout_components",
                            defaultValue: "Resolve message",
                          })}
                      </Text>
                    </Flex>
                    <ArrowRight size={18} />
                  </Flex>
                </ActionButton>
              )}
            </Flex>
          </Flex>

          <View UNSAFE_className={styles.mobile_detail_body}>
            <iframe
              key={state.selectedDataRow?.id || "mobile-detail-iframe"}
              {...(messageType !== "concept"
                ? { src: getMessagePreviewUrl(state.selectedDataRow?.id) }
                : {
                  srcDoc: state.messageContent.messageTemplate?.BODY?.string,
                })}
              style={{
                width: "100%",
                height: `${mobileDetailIframeHeight}px`,
                border: "none",
                backgroundColor: "#fff",
              }}
              title="Email Content"
              sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
              scrolling="no"
              onLoad={(event) => {
                const iframe = event.currentTarget;
                resizeMobileDetailIframe(iframe);
                window.setTimeout(() => resizeMobileDetailIframe(iframe), 200);
                window.setTimeout(() => resizeMobileDetailIframe(iframe), 800);
              }}
            />
          </View>
        </View>

        <View UNSAFE_className={styles.mobile_detail_bottom_stack}>
          <Flex
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            UNSAFE_className={styles.mobile_detail_pager}
          >
            <button
              type="button"
              className={styles.mobile_detail_pager_button}
              onClick={previousNavigation}
              disabled={state.navigationIndex <= 0}
            >
              <ArrowLeft size={16} />
              <span>{t("previous", { ns: "layout_components", defaultValue: "Previous" })}</span>
            </button>
            <Text UNSAFE_className={styles.mobile_detail_pager_count}>
              {getNavigationPositionLabel()}
            </Text>
            <button
              type="button"
              className={styles.mobile_detail_pager_button}
              onClick={nextNavigation}
              disabled={state.navigationIndex >= state.rowsData.length - 1}
            >
              <span>{t("next", { ns: "layout_components", defaultValue: "Next" })}</span>
              <ArrowRight size={16} />
            </button>
          </Flex>

          <Flex
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-around"}
            UNSAFE_className={styles.mobile_detail_action_bar}
          >
            {showReplyAction && (
              <button
                type="button"
                className={styles.mobile_detail_action}
                onClick={() => handleReplyClick("REPLY")}
              >
                <ReplyIcon size={20} />
                <span>{t("reply", { ns: "layout_components", defaultValue: "Reply" })}</span>
              </button>
            )}
            {showReplyAction && (
              <button
                type="button"
                className={styles.mobile_detail_action}
                onClick={() => handleReplyClick("REPLYALL")}
              >
                <ReplyAllIcon size={20} />
                <span>{t("reply_all", { ns: "layout_components", defaultValue: "Reply All" })}</span>
              </button>
            )}
            {showForwardAction && (
              <button
                type="button"
                className={styles.mobile_detail_action}
                onClick={() => handleForwardClick()}
              >
                <ArrowForwardIcon size={20} />
                <span>{t("forward", { ns: "layout_components", defaultValue: "Forward" })}</span>
              </button>
            )}
            {showCommentAction && (
              <button
                type="button"
                className={styles.mobile_detail_action}
                onClick={() => handleCommentClick()}
              >
                <CommentIcon size={16} height={16} color="#1976D2" />
                <span>{t("comment", { ns: "layout_components", defaultValue: "Comment" })}</span>
              </button>
            )}
            <button type="button" className={styles.mobile_detail_action}>
              <AIChatIconColored size={20} height={16} />
              <span>{t("AI chat", { ns: "layout_components", defaultValue: "AI chat" })}</span>
            </button>
            {showDeleteAction && (
              <button
                type="button"
                className={styles.mobile_detail_action}
                onClick={() =>
                  shouldConfirmDelete
                    ? setIsDeleteDialogOpen(true)
                    : state.selectedDataRow?.id && handleDeleteMessage(state.selectedDataRow.id)
                }
              >
                <TrashIcon size={18} />
                <span>{t("Delete", { ns: "layout_components", defaultValue: "Delete" })}</span>
              </button>
            )}
            {showMarkDoneAction && (
              <button
                type="button"
                className={`${styles.mobile_detail_action} ${styles.mobile_detail_action_mark_done}`}
                onClick={() => handleMarkDone()}
              >
                <Check2Circle size={18} />
                <span>{t("mark_as_done", { ns: "layout_components", defaultValue: "Mark as done" })}</span>
              </button>
            )}
          </Flex>
        </View>
      </View>
    );
  };

  useEffect(() => {
    // Refresh unread counts only when a message detail is opened (not when closed)
    if (state.showDetail) {
      refreshMessageCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showDetail]);

  useEffect(() => {
    // Mirror MessageListComponent behavior: if a message was sent while this component
    // is mounted, reload the list once so the new item appears immediately.
    // Keep the global Communication "messages" view scoped to no-context/default-inbox
    // messages only. When a specific context was selected during compose, do not
    // force this global list to reload and surface that context-bound email here.
    const needsReload = sessionStorage.getItem("messageListNeedsReload");
    const reloadContext = sessionStorage.getItem("messageListReloadContext");
    if (needsReload !== "true") {
      return;
    }
    sessionStorage.removeItem("messageListNeedsReload");
    sessionStorage.removeItem("messageListReloadContext");

    const normalizedReloadContext = normalizeAtollonNumericId(reloadContext);
    const normalizedDefaultInboxNode = normalizeAtollonNumericId(
      state.messageNodeId,
    );

    if (
      messageType === "messages" &&
      !customTreeHandle &&
      normalizedReloadContext != null &&
      normalizedReloadContext !== normalizedDefaultInboxNode
    ) {
      return;
    }

    reloadMessageList();
    refreshMessageCounts();
  }, [
    customTreeHandle,
    messageType,
    refreshMessageCounts,
    reloadMessageList,
    state.messageNodeId,
  ]);

  // Register stable action handler wrappers with parent (CommunicationList)
  // Uses ref delegation so parent never needs to re-render when state changes
  useEffect(() => {
    onRegisterHandlers?.({
      onNewEmail: () => _handlersRef.current.onNewEmail(),
      onReply: () => _handlersRef.current.onReply(),
      onReplyAll: () => _handlersRef.current.onReplyAll(),
      onForward: () => _handlersRef.current.onForward(),
      onRefresh: () => _handlersRef.current.onRefresh?.(),
      onOpenSearch: () => _handlersRef.current.onOpenSearch?.(),
      onOpenFilter: () => _handlersRef.current.onOpenFilter?.(),
      onComment: () => _handlersRef.current.onComment?.(),
      onResolveTrigger: () => _handlersRef.current.onResolveTrigger(),
      onDeleteTrigger: () => _handlersRef.current.onDeleteTrigger(),
      onMoveTrigger: () => _handlersRef.current.onMoveTrigger?.(),
      // Resolve / Mark-Done: only expose to the toolbar when NOT in bulk so
      // a stale ref cannot surface a per-message CTA while bulk is active.
      showReplyAction: !hasBulkSelection && showReplyAction,
      showForwardAction: !hasBulkSelection && showForwardAction,
      showCommentAction: !hasBulkSelection && showCommentAction,
      showDeleteAction: hasBulkSelection || showDeleteAction,
      shouldConfirmDelete,
      showResolveAction: !hasBulkSelection && showResolveAction,
      showMarkDoneAction: !hasBulkSelection && showMarkDoneAction,
      showMoveAction: hasBulkSelection || showMoveAction,
      bulkSelectedCount,
    });
    return () => onRegisterHandlers?.(null);
  }, [
    onRegisterHandlers,
    shouldConfirmDelete,
    showCommentAction,
    showDeleteAction,
    showForwardAction,
    showMarkDoneAction,
    showMoveAction,
    showReplyAction,
    showResolveAction,
    hasBulkSelection,
    bulkSelectedCount,
  ]);

  useEffect(() => {
    onRegisterMoveMessage?.(handleMoveMessage);
    return () => onRegisterMoveMessage?.(null);
  }, [handleMoveMessage, onRegisterMoveMessage]);

  // Effect to handle mobile menu state when showing detail view
  useEffect(() => {
    if (!isCompact) return;

    if (state.showDetail) {
      setShowMobileMenu?.(false);
      publish(AtollonEvent.COMMUNICATION_MOBILE_MENU, { show: false });
    } else {
      setShowMobileMenu?.(true);
      publish(AtollonEvent.COMMUNICATION_MOBILE_MENU, { show: true });
    }
  }, [state.showDetail, isCompact, setShowMobileMenu]);

  useEffect(() => {
    onDetailVisibilityChange?.(state.showDetail);
  }, [state.showDetail, onDetailVisibilityChange]);

  useEffect(() => {
    if (!state.showDetail || !useMobileCompactLayout) return;
    setMobileDetailIframeHeight(520);
  }, [state.selectedDataRow?.id, state.showDetail, useMobileCompactLayout]);

  const renderContent = () => {
    if (!state.showDetail) {
      return renderMessageList();
    } else if (useMobileCompactLayout) {
      return renderMessageListDetailPhone();
    } else if (isCompact) {
      return renderMessageListDetailMobile();
    } else {
      return renderMessageListDetailLarge();
    }
  };

  const closeMessageDialog = (reload: Boolean) => {
    actions.setShowNewMessageDialog(false);
    // Clear reply state when dialog is closed
    actions.setReplyType("");
    actions.setReplyMessageObj(null);
    actions.setReplyEmailAttachments([]);
    actions.setComposeMessageType("");

    if (reload) {
      reloadMessageList();
    }
  };

  // Update ref every render so stable wrappers always call the latest state
  _handlersRef.current = {
    onNewEmail: () => {
      openNewEmailComposer();
    },
    onRefresh: () => {
      reloadMessageList();
      refreshMessageCounts();
    },
    onOpenSearch: () => {
      setIsSearchDialogOpen(true);
    },
    onOpenFilter: () => {
      setIsFilterDialogOpen(true);
    },
    onReply: () => {
      if (!hasBulkSelection && showReplyAction) {
        handleReplyClick("REPLY");
      }
    },
    onReplyAll: () => {
      if (!hasBulkSelection && showReplyAction) {
        handleReplyClick("REPLYALL");
      }
    },
    onForward: () => {
      if (!hasBulkSelection && showForwardAction) {
        handleForwardClick();
      }
    },
    onComment: () => {
      if (!hasBulkSelection && showCommentAction) {
        handleCommentClick();
      }
    },
    onResolveTrigger: () => {
      // Resolve / Mark-Done operate on the currently opened message only.
      // In bulk mode the detail is suppressed (see isBulkDetailSuppressed),
      // so a resolve trigger here would act on a stale selection — guard
      // against it regardless of what the parent toolbar exposes.
      if (hasBulkSelection) return;

      if (showMarkDoneAction) {
        handleMarkDone();
        return;
      }

      if (showResolveAction) {
        openResolveOptionsScreen();
      }
    },
    onDeleteTrigger: () => {
      if (hasBulkSelection) {
        handleBulkDeleteMessages();
        return;
      }

      if (!showDeleteAction || !state.selectedDataRow?.id) {
        return;
      }

      if (shouldConfirmDelete) {
        setIsDeleteDialogOpen(true);
      } else {
        handleDeleteMessage(state.selectedDataRow.id);
      }
    },
    onMoveTrigger: () => {
      if (hasBulkSelection) {
        openMoveDialog();
        return;
      }

      if (!showMoveAction || !state.selectedDataRow?.id) {
        return;
      }

      openMoveDialog();
    },
    showReplyAction: !hasBulkSelection && showReplyAction,
    showForwardAction: !hasBulkSelection && showForwardAction,
    showCommentAction: !hasBulkSelection && showCommentAction,
    showDeleteAction: hasBulkSelection || showDeleteAction,
    shouldConfirmDelete,
    // Resolve / Mark-Done are per-message; hide them entirely while bulk is
    // active so the top toolbar only exposes bulk-safe actions (archive + delete).
    showResolveAction: !hasBulkSelection && showResolveAction,
    showMarkDoneAction: !hasBulkSelection && showMarkDoneAction,
    showMoveAction: hasBulkSelection || showMoveAction,
    bulkSelectedCount,
  };

  if (state.showLoader) {
    return <Loader />;
  } else {
    return (
      <>
        {state.rowsData?.length > 0 ? (
          <div ref={refs.componentRef} style={{ height: "100%" }}>
            {isCompact ? (
              renderContent()
            ) : (
              <Grid
                areas={["list detail"]}
                columns={["35%", "auto"]}
                rows={["1fr"]}
                width={"100%"}
                UNSAFE_className={styles.message_list_wrapper}
                UNSAFE_style={{
                  height: "100%",
                  overflow: "hidden",
                  gap: "8px",
                  padding: "8px",
                  boxSizing: "border-box",
                  backgroundColor: "#f5f6f9",
                }}
              >
                {/* ── LEFT PANEL: Message list ── */}
                <View
                  gridArea={"list"}
                  UNSAFE_className={styles.message_list_parent}
                  UNSAFE_style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  {/* Tab strip: All / Internal / External / AI chat */}
                  <Flex
                    direction={"row"}
                    alignItems={"center"}
                    gap="5%"
                    UNSAFE_style={{
                      padding: "0 8px",
                      height: "44px",
                      flexShrink: 0,
                      borderBottom: "1px solid #eceff1",
                      color: "#728289",
                    }}
                  >
                    {[
                      {
                        key: "all",
                        label: "All",
                        color: "#1976D2",
                        count: state.totalMessageListRec,
                      },
                      {
                        key: "internal",
                        label: "Internal",
                        color: "#F72B1C",
                        count: state.totalMessageListRec,
                      },
                      {
                        key: "external",
                        label: "External",
                        color: "#8BC34A",
                        count: state.totalMessageListRec,
                      },
                      {
                        key: "ai_chat",
                        label: "AI chat",
                        color: "#14AFF5",
                        count: state.totalMessageListRec,
                      },
                    ].map((tab) => (
                      <Flex
                        key={tab.key}
                        direction={"row"}
                        alignItems={"center"}
                        gap={"6px"}
                        UNSAFE_style={{ cursor: "default", padding: "0 4px" }}
                      >
                        <span
                          style={{
                            fontSize: "14px",
                            color: tab.key === "all" ? "#1976d2" : "#728289",
                            fontWeight: tab.key === "all" ? 600 : 400,
                          }}
                        >
                          {tab.label}
                        </span>
                        {tab.count != null && (
                          <Flex
                            direction={"column"}
                            width={"28px"}
                            alignItems={"center"}
                            justifyContent={"center"}
                            UNSAFE_style={{
                              backgroundColor: tab.color,
                              borderRadius: "32px",
                              color: "#fff",
                              fontSize: "11px",
                              fontWeight: "bold",
                              padding: "5px 0",
                            }}
                          >
                            {tab.count > 999 ? "999+" : tab.count}
                          </Flex>
                        )}
                      </Flex>
                    ))}
                  </Flex>
                  {/* Message list */}
                  <View
                    flex={1}
                    overflow={"auto"}
                    width={"100%"}
                    UNSAFE_style={{ minHeight: 0 }}
                  >
                    <BulkSelectionProvider
                      selectedKeys={bulkSelectedKeys}
                      toggle={handleBulkSelectionChange}
                    >
                      <ListBox
                        autoFocus
                        id="listMessageCards"
                        selectionMode="single"
                        onSelectionChange={handleListRowClick}
                        defaultSelectedKeys={state.selectedKeys}
                        selectedKeys={state.selectedKeys}
                        aria-label="Message List Component"
                        items={listMessageList.items}
                        isLoading={listMessageList.isLoading}
                        onLoadMore={
                          state.totalMessageListRec > state.rowsData.length
                            ? listMessageList.loadMore
                            : undefined
                        }
                        width={"100%"}
                        maxHeight={"100vh"}
                        UNSAFE_style={{ padding: "0 0 0 5px", minHeight: 0 }}
                      >
                        {(item) => (
                          <Item key={item.id}>
                            <div
                              draggable={messageType !== "concept" && !!onDesktopRowDragStart}
                              onDragStart={(event) => {
                                if (!onDesktopRowDragStart || messageType === "concept") {
                                  return;
                                }
                                event.dataTransfer.effectAllowed = "move";
                                event.dataTransfer.setData("text/plain", String(item.id));
                                onDesktopRowDragStart(item);
                              }}
                              onDragEnd={() => onDesktopRowDragStart?.(null)}
                            >
                              {messageType !== "concept" ? (
                                <CardComponentLarge
                                  item={item}
                                  showUnresolvedReferenceIndicator={messageType === "todo"}
                                />
                              ) : (
                                <ConceptCardComponent item={item} />
                              )}
                            </div>
                          </Item>
                        )}
                      </ListBox>
                    </BulkSelectionProvider>
                  </View>
                </View>

                {/* ── RIGHT PANEL: Email detail ── */}
                {state.showLoaderDetail ? (
                  <View
                    gridArea={"detail"}
                    UNSAFE_className={styles.message_list_detail_wrapper}
                    UNSAFE_style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      backgroundColor: "#fff",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <Loader />
                  </View>
                ) : (
                  <View
                    gridArea={"detail"}
                    UNSAFE_className={styles.message_list_detail_wrapper}
                    UNSAFE_style={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      backgroundColor: "#fff",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    {(showResolveAction || showMarkDoneAction) && (
                      <Flex
                        direction={"row"}
                        width={"100%"}
                        alignItems={"center"}
                        justifyContent={"start"}
                        UNSAFE_style={{
                          padding: "12px 16px 0 16px",
                          flexShrink: 0,
                          backgroundColor: "#fff",
                        }}
                      >
                        <button
                          type="button"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "12px",
                            width: "100%",
                            minHeight: "46px",
                            padding: "0 16px",
                            borderRadius: "12px",
                            fontSize: "14px",
                            fontWeight: 600,
                            cursor: "pointer",
                            border: showMarkDoneAction
                              ? "1px solid #1976d2"
                              : "1px solid #ff9800",
                            color: showMarkDoneAction ? "#1976d2" : "#ff9800",
                            background: showMarkDoneAction
                              ? "#f5f9ff"
                              : "#fff8ef",
                          }}
                          onClick={() => {
                            if (showMarkDoneAction) {
                              handleMarkDone();
                              return;
                            }

                            openResolveOptionsScreen();
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            {showMarkDoneAction ? (
                              <Check2Circle size={18} />
                            ) : (
                              <QuestionCircle size={18} />
                            )}
                            <span>
                              {showMarkDoneAction
                                ? t("mark_as_done", {
                                  ns: "layout_components",
                                  defaultValue: "Mark as done",
                                })
                                : t("Resolve message", {
                                  ns: "layout_components",
                                  defaultValue: "Resolve message",
                                })}
                            </span>
                          </span>
                          <ArrowRight size={18} />
                        </button>
                      </Flex>
                    )}
                    {/* Email sender / subject / recipient metadata */}
                    <Flex
                      direction={"column"}
                      width={"100%"}
                      alignItems={"start"}
                      justifyContent={"start"}
                      UNSAFE_className={styles.email_content_header_parent}
                      UNSAFE_style={{
                        borderBottom: "1px solid #eceff1",
                        flexShrink: 0,
                      }}
                    >
                      <Flex
                        direction={"row"}
                        alignSelf={"stretch"}
                        alignItems={"start"}
                        justifyContent={"start"}
                        gap={"18px"}
                      >
                        <View UNSAFE_className={styles.user_img}>
                          <img
                            id="img_MessageList"
                            className={styles.avatar_desktop_light}
                            alt=""
                            src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                          />
                        </View>
                        <Flex
                          direction={"column"}
                          width={"100%"}
                          alignItems={"start"}
                          justifyContent={"start"}
                          gap={"5px"}
                        >
                          <Flex
                            direction={"row"}
                            alignSelf={"stretch"}
                            alignItems={"end"}
                            justifyContent={"space-between"}
                          >
                            {messageType !== "concept" && (
                              <Flex
                                direction={"row"}
                                alignItems={"center"}
                                justifyContent={"start"}
                                gap={"8px"}
                              >
                                <View UNSAFE_className={styles.user_full_name}>
                                  {utils.getSenderName(
                                    state.selectedDataRow.sender,
                                  )}
                                </View>
                                <View
                                  width={"1px"}
                                  position={"relative"}
                                  height={"12px"}
                                  UNSAFE_className={styles.user_detail_divider}
                                />
                                <Flex
                                  direction={"row"}
                                  alignItems={"center"}
                                  gap={"size-50"}
                                >
                                  <View UNSAFE_className={styles.user_email}>
                                    {utils.getSenderEmail(
                                      state.selectedDataRow.mailFrom,
                                    )}
                                  </View>
                                  <EmailSenderContactBadge
                                    messageRow={senderContactBadgeRow}
                                    mailFrom={state.selectedDataRow.mailFrom}
                                    sender={state.selectedDataRow.sender ?? ""}
                                    enabled={Boolean(state.selectedDataRow?.mailFrom)}
                                  />
                                </Flex>
                              </Flex>
                            )}
                            <View
                              position={"relative"}
                              UNSAFE_className={styles.div_date}
                            >
                              {moment(state.selectedDataRow.date).format(
                                "DD.MM.YYYY HH:mm",
                              )}
                            </View>
                          </Flex>
                          <Flex
                            direction={"column"}
                            alignSelf={"stretch"}
                            alignItems={"start"}
                            justifyContent={"start"}
                            gap={"8px"}
                            UNSAFE_className={styles.subject_parent}
                          >
                            <View
                              position={"relative"}
                              alignSelf={"stretch"}
                              UNSAFE_className={styles.email_subject}
                            >
                              {state.selectedDataRow.subject}
                            </View>
                            <RecipientToField
                              recipient={resolveRecipientForDisplay(
                                state.selectedDataRow?.id,
                                state.selectedDataRow?.RECIPIENT,
                                state.messageContent?.MESSAGE,
                              )}
                            />
                            <ContextPathLinks
                              message={state.messageContent.MESSAGE}
                              styles={styles}
                            />
                            <RecipientNotifyField
                              recipient={resolveNotifyRecipientForDisplay(
                                state.selectedDataRow?.id,
                                state.messageContent?.MESSAGE,
                              )}
                            />
                            {state.messageContent.participantsName &&
                              isMeetingMessage && (
                                <Flex
                                  direction={"row"}
                                  alignItems={"center"}
                                  justifyContent={"start"}
                                  gap={"6px"}
                                  alignSelf={"stretch"}
                                >
                                  <View UNSAFE_className={styles.address}>
                                    {t("participants", {
                                      ns: "layout_components",
                                      defaultValue: "Participants",
                                    })}
                                    :&nbsp;
                                    {state.messageContent.participantsName}
                                  </View>
                                </Flex>
                              )}
                            <MessageAttachment
                              store={store}
                              styles={styles}
                              messageContent={state.messageContent}
                            />
                          </Flex>
                        </Flex>
                      </Flex>
                    </Flex>

                    {/* Email body iframe — grows to fill all remaining space */}
                    <View
                      UNSAFE_className={styles.email_content}
                      flex={1}
                      minHeight={0}
                      UNSAFE_style={{ overflow: "hidden", paddingTop: "16px", paddingLeft: "8px" }}
                    >
                      <iframe
                        {...(messageType !== "concept"
                          ? { src: getMessagePreviewUrl(state.selectedDataRow?.id) }
                          : {
                            srcDoc:
                              state.messageContent.messageTemplate?.BODY?.string,
                          })}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          backgroundColor: "#fff",
                        }}
                        title="Email Content"
                        sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
                      />
                    </View>

                    {/* Previous / Next navigation */}
                    <Flex
                      direction={"row"}
                      width={"100%"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      marginTop={"size-200"}
                      marginBottom={"size-200"}
                      UNSAFE_style={{ flexShrink: 0 }}
                    >
                      <Flex
                        direction={"row"}
                        width={"258px"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        gap={"32px"}
                      >
                        {state.navigationIndex > 0 && (
                          <Flex
                            direction={"row"}
                            height={"32px"}
                            alignItems={"center"}
                            justifyContent={"center"}
                            gap={"8px"}
                            UNSAFE_className={styles.btn_previous}
                          >
                            <Button
                              variant="primary"
                              onPress={previousNavigation}
                              UNSAFE_style={{
                                background: "transparent",
                                border: "none",
                                width: "100%",
                              }}
                            >
                              <>
                                <ArrowLeft className={styles.icon_navigate} />
                                &nbsp;&nbsp;&nbsp;
                                <View UNSAFE_className={styles.btn_text}>
                                  {t("previous", { ns: "layout_components" })}
                                </View>
                              </>
                            </Button>
                          </Flex>
                        )}
                        {state.navigationIndex <= state.rowsData.length - 1 &&
                          state.rowsData.length > 0 && (
                            <Flex
                              direction={"row"}
                              width={"113px"}
                              height={"32px"}
                              flexShrink={0}
                              alignItems={"center"}
                              justifyContent={"center"}
                              gap={"8px"}
                              UNSAFE_className={styles.btn_next}
                            >
                              <Button
                                id="btn_next_message_detail"
                                variant="primary"
                                onPress={nextNavigation}
                                UNSAFE_style={{
                                  background: "transparent",
                                  border: "none",
                                  width: "100%",
                                }}
                              >
                                <>
                                  <View UNSAFE_className={styles.btn_text}>
                                    {t("next", { ns: "layout_components" })}
                                  </View>
                                  &nbsp;&nbsp;&nbsp;
                                  <ArrowRight
                                    className={styles.icon_navigate}
                                  />
                                </>
                              </Button>
                            </Flex>
                          )}
                      </Flex>
                    </Flex>
                  </View>
                )}
              </Grid>
            )}
          </div>
        ) : (
          <View
            position={"relative"}
            top={0}
            left={0}
            width={"100%"}
            height={"100%"}
            overflow={"hidden"}
            UNSAFE_className={styles.message_list_parent}
          >
            <Flex
              width={"100%"}
              height={"100%"}
              direction={"column"}
              alignItems={"center"}
              justifyContent={"center"}
              UNSAFE_style={{ minHeight: 0 }}
            >
              <NoResultsMessage />
            </Flex>
          </View>
        )}
        {state.showNewMessageDialog && (
          <NewMessageComponent
            isOpen={state.showNewMessageDialog}
            windowIndex={0}
            selectedItemId={state.messageNodeId}
            composeFolderContextId={customTreeHandle}
            defaultInboxMessageNodeId={state.messageNodeId}
            defaultContextId={resolveContextIdForMessage(state.replyMessageObj)}
            defaultContextType={resolveContextTypeForMessage(state.replyMessageObj)}
            replyType={state.replyType}
            messageObj={state.replyMessageObj}
            emailAttachments={state.replyEmailAttachments}
            forcedMessageType={state.composeMessageType}
            messageType={messageType}
            closeMessageDialog={closeMessageDialog}
          />
        )}
        {isCommentChatOpen && commentChatTarget && (
          <CommentChatWindow
            isOpen={isCommentChatOpen}
            targetMessage={commentChatTarget}
            currentContextId={String(resolveContextIdForMessage(state?.messageContent?.MESSAGE))}
            onClose={() => {
              setIsCommentChatOpen(false);
              setCommentChatTarget(null);
              setCommentChatCurrentContextId(undefined);
            }}
          />
        )}
        <DialogContainer
          onDismiss={() => setIsDeleteDialogOpen(false)}
          isDismissable
        >
          {isDeleteDialogOpen && (
            <MessageDialog close={() => setIsDeleteDialogOpen(false)} />
          )}
        </DialogContainer>
        <DialogContainer
          onDismiss={() => setIsResolveOptionsOpen(false)}
          isDismissable
        >
          {isResolveOptionsOpen && (
            <ResolveMessageOptionsDialog
              onClose={() => setIsResolveOptionsOpen(false)}
              visibility={{
                reply: showReplyAction,
                replyAll: showReplyAction,
                forward: showForwardAction,
                comment: showCommentAction,
                move: showMoveAction,
              }}
              onReply={() => handleReplyClick("REPLY")}
              onReplyAll={() => handleReplyClick("REPLYALL")}
              onForward={() => {
                void handleForwardClick();
              }}
              onComment={() => {
                void handleCommentClick();
              }}
              onMoveToContext={() => openMoveDialog()}
            />
          )}
        </DialogContainer>
        <DialogContainer
          onDismiss={() => setIsSearchDialogOpen(false)}
          isDismissable
        >
          {isSearchDialogOpen && (
            <SearchDialog close={() => setIsSearchDialogOpen(false)} />
          )}
        </DialogContainer>
        <DialogContainer
          onDismiss={() => setIsFilterDialogOpen(false)}
          isDismissable
        >
          {isFilterDialogOpen && (
            <FilterDialog close={() => setIsFilterDialogOpen(false)} />
          )}
        </DialogContainer>
        <DialogContainer
          onDismiss={() => {
            setIsMoveDialogOpen(false);
          }}
        >
          {isMoveDialogOpen && (
            <ContextMoveDialog
              onClose={() => setIsMoveDialogOpen(false)}
              parentItem={null}
              selectedItem={null}
              showActivities
              showProjects
              showFolders
              selectedItemType={FPADataTypes.FOLDER}
              onMoveContextSelected={async (selectedContextItem) => {
                var sel_treeNode = '';
                if (selectedContextItem) {
                  switch (selectedContextItem.type) {
                    case FPADataTypes.FOLDER:
                      var folder = await projectService.getFolder(new GetFolderRequest(store.Server, store.SessionId, Number(selectedContextItem.id)));
                      if (folder) {
                        sel_treeNode = folder.FOLDER.messageDefault;
                      }
                      break;
                    case FPADataTypes.PROJECT:
                      var project = await projectService.getProject(new GetProjectRequest(store.Server, store.SessionId, Number(selectedContextItem.id)));
                      if (project) {
                        sel_treeNode = project.PROJECT.defaultMessageContainer ?? '';
                      }
                      break;
                    case FPADataTypes.ACTIVITY:
                      sel_treeNode = String(selectedContextItem.id) ?? '';
                      break;
                    default:
                      sel_treeNode = '';
                      break;
                  }
                }
                const moved = await handleMoveSelectedMessage(sel_treeNode);
                return {
                  moved,
                  error: moved ? "" : "move_failed",
                  itemId: Number(selectedContextItem.id) || 0,
                };
              }}
            />
          )}
        </DialogContainer>
      </>
    );
  }
};

export const MessagesComponent = LayoutComponent(MessagesList);
